// ITEM 14(b) — **THE STRINGS LAYER. THE HOUSE'S OWN COPY, IN BOTH LANGUAGES, IN ONE PLACE.**
//
// The build plan said `Record<Key, {en, es}>` in TypeScript "so the TYPE refuses a half-translated
// key" (R42's totality argument, which is what makes `PAGE_TITLES` safe). **THAT FORK WAS REACHED AND
// NOT TAKEN, and the reason is a boundary this house already declares:** `src/lib/*.ts` is CLIENT-ONLY
// and the SERVER composes member-facing copy too — every refusal sentence, every confirm, every
// ledger line. A law both sides share lives in `src/*.js`. So the totality is enforced by the GATE
// instead of by tsc: `stringsViolations` is the law and `scripts/item14-strings.mjs` RUNS it, both
// directions, on every build. A check written for tsc's job is the first thing in this file, not the
// last.
//
// FOUR LAWS LIVE IN HERE AND EACH ONE IS A SHAPE THIS REPO HAS ALREADY PAID FOR:
//
//   1. **TOTALITY, BOTH DIRECTIONS.** Every key carries every language, and no key carries a language
//      this house does not speak. One direction alone is R28/R29's disease: a key that quietly holds
//      only English renders in English to a member who asked for Spanish, and nothing anywhere says so.
//
//   2. **PLACEHOLDER PARITY, WHICH IS THE HALF TOTALITY CANNOT SEE.** "Ends {date}" translated as
//      "Termina pronto" is a complete, present, non-empty Spanish string that has silently dropped a
//      FACT. The `{token}` set must be identical across every language of a key.
//
//   3. **AN UNTRANSLATED VALUE IS A VIOLATION, AND THE EXCEPTIONS ARE DECLARED** (R28/R29/R30's fix
//      shape). English pasted into the `es` slot passes totality and passes parity and is exactly what
//      a half-finished translation looks like. So identical values are RED unless the key is on
//      `SAME_IN_BOTH` — a proper noun, a product name, a number. Declaring the other side by name is
//      what makes "this key is deliberately the same" different from "nobody got to this key".
//
//   4. **AN UNKNOWN KEY IS VISIBLE, NEVER SILENT** (R17). `t` hands back the key itself, because a
//      missing string that renders as "" is a blank space in a sentence nobody can report. A key
//      whose language is missing falls back to English — a member must always have words — and the
//      GATE is what stops that fallback ever being reached in production. That split is this house's
//      own: `isLanguage` REFUSES at the write door, `normalizeLanguage` RESOLVES on a read.
//
// **WHAT IS DELIBERATELY NOT IN HERE, AND IT IS THE SCOPE QUESTION FLAGGED FOR HER WORD:** a member's
// OWN words. Books, journal entries and everything members write in the Living Room are not house
// copy, and translating them is a different act entirely. Nothing in this catalog touches them.
// Neither does it touch the SERVER's composed sentences (part (d)), or `src/theme.js`'s labels, which
// stay where the theme law owns them rather than being copied to a second answer key here (R13).

import { LANGUAGES, DEFAULT_LANGUAGE, normalizeLanguage } from "./language.js";

// ---------------------------------------------------------------------------------------------
// THE CATALOG. Every key carries every language. The gate holds that in both directions.
//
// The slice is the MEMBER JOURNEY's account surfaces — the menu she opens from every page, what it
// says about what she is on, and the switcher itself. Her order names the journey; scope is hers to
// widen, and widening it is adding rows here rather than adding a mechanism.
// ---------------------------------------------------------------------------------------------
export const STRINGS = {
  // ---- FUNNEL-4: Lala speaks first, and she opens with their name ----
  // Her amended clause 2: warm and natural, never "Hello John [account]". The name rides as {name}
  // and a translation that drops it is complete, non-empty and missing the only personal fact in the
  // line — which on the FIRST sentence a stranger ever reads is the whole hook.
  "lala.opening.greeting": { en: "Hey {name}.", es: "Hola {name}." },
  "lala.opening.chartInHand": { en: "I have your chart right here.", es: "Tengo tu carta astral aquí mismo." },
  "lala.opening.ask": { en: "Is there anything you want to know?", es: "¿Hay algo que quieras saber?" },

  // ITEM 2 — THE WAIT BEFORE THE WINDOW. Her clause: the beautiful wait, house voice, NEVER
  // machinery (S1-A/R117) — a stranger does not have a chart being cast, she has her chart being
  // pulled. No table, no vendor, no status, and no em dash in either language (ITEM 14b).
  // ITEM 2's wait state says its own words through `TALK_STAGES`, which is why there is exactly ONE
  // row here rather than three: a second wait line beside that rotation is two answers to *what the
  // wait says* (R28/R29), and ITEM 14b's own law is that a row nobody reads is a translation no
  // member will ever see. This one IS read — by the SERVER, at the turn refusal.
  "talk.chartWait.refused": { en: "One moment, love. I am still pulling your chart, and I want it in my hands before I answer you.", es: "Un momento, mi amor. Todavía estoy trayendo tu carta, y la quiero en mis manos antes de responderte." },

  // ---- FUNNEL-4: the five free turns with Lala (the counter and the warm wall) ----
  // Her words, and the counter draws NOTHING at zero — at zero she is at the wall, and a counter
  // reading "0 more with me" beside a wall is the house saying the same thing twice.
  "lala.free.counterOne": { en: "1 more with me, love", es: "1 más conmigo, mi amor" },
  "lala.free.counterMany": { en: "{n} more with me, love", es: "{n} más conmigo, mi amor" },
  // The wall, in her voice, at the exact moment desire is highest. It says what she is being invited
  // INTO rather than what has run out (S1-A/R117: name the act, never the machinery).
  "lala.free.wallTitle": { en: "I'm just getting started with you.", es: "Apenas estoy empezando contigo." },
  "lala.free.wallBody": { en: "Come all the way in.", es: "Entra del todo." },
  "lala.free.wallAction": { en: "Come all the way in", es: "Entra del todo" },

  // ---- The account menu (the one control on every page of this site) ----
  "account.pill.member": { en: "Member", es: "Miembro" },
  "account.pill.account": { en: "Account", es: "Cuenta" },
  "account.pill.logIn": { en: "Log in", es: "Iniciar sesión" },
  "account.signedInAs": { en: "Signed in as", es: "Sesión iniciada como" },
  "account.myRoom": { en: "My Room", es: "Mi Cuarto" },
  // Her menu link (2026-10-08): the wing's own name, which stays the same in every language.
  "account.truth": { en: "12th House Truth", es: "12th House Truth" },
  "account.upgrade": { en: "Upgrade", es: "Subir de nivel" },
  "account.chooseMembership": { en: "Choose your membership", es: "Elige tu membresía" },
  "account.manageBilling": { en: "Manage billing", es: "Gestionar la facturación" },
  // P2 (her item 9 choice, "YES, GENTLY"): the soft backup-card prompt.
  "account.backupCardNote": { en: "Paying by debit, prepaid or Link? A backup card keeps your membership going if a payment doesn't go through.", es: "¿Pagas con débito, prepago o Link? Una tarjeta de respaldo mantiene tu membresía activa si un pago no se completa." },
  "account.addBackupCard": { en: "Add a backup card", es: "Añadir una tarjeta de respaldo" },
  "account.openingBilling": { en: "Opening billing...", es: "Abriendo la facturación..." },
  "account.dashboard": { en: "Dashboard", es: "Panel" },
  "account.logOut": { en: "Log out", es: "Cerrar sesión" },

  // ---- What the menu says about what she is on (D2-1's gift-truth block) ----
  "sub.heading": { en: "Subscription", es: "Suscripción" },
  "sub.free": { en: "Free", es: "Gratis" },
  // The price itself is composed by the SERVER and passes through untouched; only the per-month
  // wrapper is ours. The `{price}` token is why placeholder parity is law and not a nicety.
  "sub.perMonth": { en: "{price}/month", es: "{price}/mes" },
  "sub.endsOn": {
    en: "Ends {date}. Everything stays yours until then.",
    es: "Termina el {date}. Todo sigue siendo tuyo hasta entonces.",
  },
  "sub.spiritKeeping": {
    en: "Spirit Keeping is yours, for good.",
    es: "Spirit Keeping es tuyo, para siempre.",
  },
  // R87 — the two things this panel can say when the one read that answers "what has she paid for"
  // did not land. It says nothing about her tier, because it does not know it; it says what is
  // missing and that her account is untouched, which is the part a member seeing "Free" over a
  // membership she pays for needs to hear first.
  "sub.readFailed": {
    en: "We could not read your membership just now. Nothing about your account has changed, and billing is back here as soon as it loads.",
    es: "No pudimos leer tu membresía en este momento. Nada de tu cuenta ha cambiado, y la facturación vuelve aquí en cuanto cargue.",
  },
  "sub.signInAgain": {
    en: "Sign in again to see your membership.",
    es: "Vuelve a iniciar sesión para ver tu membresía.",
  },
  // R212 — **WHAT THE MENU SAYS WHEN HER MEMBERSHIP IS PAUSED**, and until this round it said
  // nothing at all: a frozen year-long Vet read `Free` under *Subscription*, which is R87's own
  // reported sentence arriving through the read that LANDED. It is ONE WHOLE SENTENCE per language
  // rather than a lead and a tail concatenated at the call site — LF-1's lesson, paid on this same
  // pause one surface over: Spanish does not put its words where English puts them, and a sentence
  // assembled from fragments is a sentence nobody wrote in either language. The tier she held is the
  // bold line ABOVE this, in the SERVER's own words, so this row carries no placeholder to drop.
  //
  // It is deliberately SHORT where the purple box's `tier.frozen.lede` is three sentences: this is a
  // dropdown under a pill, and the door out of it (Manage billing) is two rows below.
  //
  // **AND THE HOUSE WRITES NO EM DASHES, IN EITHER LANGUAGE** — ITEM 14b's own census caught this
  // row in the round that wrote it, correctly, on the first gate run.
  "sub.paused": {
    en: "Paused. Your card did not go through. Nothing has been deleted. Sort the payment out and everything opens back up.",
    es: "En pausa. Tu tarjeta no pasó. No se ha borrado nada. Arregla el pago y todo se vuelve a abrir.",
  },

  // ---- 14-B2: THE FRONT DOOR. The screen every member fills in to exist here. ----
  //
  // The onboarding modal's own chrome. `HowThisWorks` holds a hook and stays in App.tsx, so its COPY
  // moved instead of the component (R37/R42's pattern: move the data) and the gate holds the rows.
  "signup.eyebrow": { en: "Welcome to 12th House", es: "Te damos la bienvenida a 12th House" },
  "signup.heading": { en: "Let's create your room", es: "Vamos a crear tu cuarto" },
  "signup.submit": { en: "Create my room", es: "Crear mi cuarto" },
  // C8b — the field says OPTIONAL on its own label, because her constraint (a) is that this is not a
  // condition of entry and a member reading a front door decides in a second whether to walk through
  // it. The placeholder shows the shape the default country code accepts, so the one refusal most
  // members could ever meet is the one the example already prevents.
  "signup.phone": { en: "Phone number (optional)", es: "Número de teléfono (opcional)" },
  "signup.phonePlaceholder": { en: "(555) 123-4567", es: "(555) 123-4567" },
  "signup.how.line1": {
    en: "Here is how this works. You put in your information, and I make you two readings right now, free: your Birth Chart and your Numerology.",
    es: "Así funciona esto. Pones tu información y te hago dos lecturas ahora mismo, gratis: tu Carta Natal y tu Numerología.",
  },
  "signup.how.line2": {
    en: "Your name has to be exactly right, the way it was given to you at birth, middle name and all. Your numerology is built from those letters, so a nickname gives you somebody else's numbers.",
    es: "Tu nombre tiene que estar exactamente bien, tal como te lo dieron al nacer, con segundo nombre y todo. Tu numerología se construye con esas letras, así que un apodo te da los números de otra persona.",
  },
  "signup.how.line3": {
    en: "Then your own room in the house is waiting, with your readings already in it.",
    es: "Después tu propio cuarto en la casa te espera, con tus lecturas ya adentro.",
  },
  "signup.how.got": { en: "Got it", es: "Entendido" },
  "signup.how.reopen": { en: "How this works", es: "Cómo funciona esto" },

  // ---- The other half of signup: the overlay her room is built behind ----
  //
  // She taps "Crear mi cuarto" and waits here for a couple of minutes. A screen that came back in
  // English at exactly that moment is the seam at its worst — the one place on the journey where she
  // cannot leave, cannot act, and has nothing to read but this.
  // The report NAMES inside it are not here: `REPORT_TYPE_LABELS` (R32) and the server already agree
  // on what a reading is called, and a second answer key for that travels with part (d).
  "build.label": { en: "Preparing your room", es: "Preparando tu cuarto" },
  "build.title": { en: "Preparing your room…", es: "Preparando tu cuarto…" },
  "build.phase1": { en: "Reading your chart and your numerology…", es: "Leyendo tu carta y tu numerología…" },
  "build.phase2": { en: "Evaluating every placement in your chart…", es: "Evaluando cada posición de tu carta…" },
  "build.phase3": { en: "Writing your readings…", es: "Escribiendo tus lecturas…" },
  "build.phase4": { en: "Building your room…", es: "Construyendo tu cuarto…" },
  "build.count": {
    en: "{ready} of {total} readings ready. These are written for you, not templated, so they take a couple of minutes.",
    es: "{ready} de {total} lecturas listas. Se escriben para ti, no son plantillas, así que tardan un par de minutos.",
  },
  "build.writing": { en: "(writing)", es: "(escribiendo)" },
  "build.noWait": {
    en: "You do not have to wait here. Step into your room and your readings will land as they finish.",
    es: "No tienes que esperar aquí. Entra a tu cuarto y tus lecturas irán llegando conforme terminen.",
  },
  "build.enter": { en: "Enter your room", es: "Entra a tu cuarto" },
  "build.readyEyebrow": { en: "Your room is ready", es: "Tu cuarto está listo" },
  // Two rows rather than one with an optional token: a name that is missing must not leave a comma
  // hanging in either language, and "she has no name on file" is a different sentence, not a blank.
  "build.welcome": { en: "Welcome home.", es: "Ya estás en casa." },
  "build.welcomeNamed": { en: "Welcome home, {name}.", es: "Ya estás en casa, {name}." },
  "build.readyHint": {
    en: "Your readings are waiting inside. Open one whenever you want it, and it is written for you then.",
    es: "Tus lecturas te esperan adentro. Abre la que quieras cuando la quieras, y se escribe para ti en ese momento.",
  },

  // ---- The form itself (R12's front door, and the ten reading forms built from the same one) ----
  //
  // **THESE ARE THE COMPONENT'S DEFAULTS, NOT A TRANSLATION DONE INSIDE IT (R24).** The copy arrives
  // at `CreateRoomForm` as PROPS typed as English literals at a dozen call sites in App.tsx, so a
  // component that translated its own text would be overruled by every one of them. The defaults are
  // resolved from here against the form's `language`; a caller that wants different words passes
  // `t(language, ...)` rather than a literal.
  "form.submit": { en: "Create your room", es: "Crea tu cuarto" },
  "form.nameHint": {
    en: "Full name at birth, including your middle name, so your numerology is accurate.",
    es: "Nombre completo al nacer, con tu segundo nombre, para que tu numerología sea exacta.",
  },
  "form.birthDate": { en: "Birth date", es: "Fecha de nacimiento" },
  "form.birthTime": { en: "Birth time", es: "Hora de nacimiento" },
  "form.birthTimeUnknown": { en: "Unknown birth time", es: "No sé mi hora de nacimiento" },
  "form.privacy": {
    en: "Your information is private and secure. We use it only to run your readings, it is visible only to you, and never shared or sold.",
    es: "Tu información es privada y segura. La usamos solo para hacer tus lecturas, solo tú puedes verla, y nunca se comparte ni se vende.",
  },
  // N19-1's two step headings. The emphasised half is its own key because it is `<strong>` in the
  // markup and the whole ruling rests on those exact words being unmissable. Both languages put the
  // emphasised phrase LAST, which is what makes a two-key split safe here rather than clever.
  "form.step1.title": { en: "Your details", es: "Tus datos" },
  "form.step1.titleStrong": { en: "AT BIRTH", es: "AL NACER" },
  "form.step1.sub": {
    en: "Your name as it was given to you at birth, and where and when you were born. Not where you live now.",
    es: "Tu nombre tal como te lo dieron al nacer, y dónde y cuándo naciste. No dónde vives ahora.",
  },
  "form.step2.title": { en: "Where you", es: "Dónde" },
  "form.step2.titleStrong": { en: "live now", es: "vives ahora" },
  "form.step2.sub": {
    en: "Your chart is cast from your birthplace, always. This is for your transits and your daily guidance, so they land on your clock and on what is moving through your houses today.",
    es: "Tu carta siempre se calcula desde tu lugar de nacimiento. Esto es para tus tránsitos y tu guía diaria, para que lleguen a tu hora y a lo que se mueve por tus casas hoy.",
  },

  // ---- The three shared field blocks every birth form on this site is built from ----
  "name.first": { en: "First name", es: "Nombre" },
  "name.middle": { en: "Middle name", es: "Segundo nombre" },
  "name.last": { en: "Last name", es: "Apellido" },
  "name.firstPlaceholder": { en: "First", es: "Nombre" },
  "name.middlePlaceholder": { en: "Middle", es: "Segundo" },
  "name.lastPlaceholder": { en: "Last", es: "Apellido" },
  "name.middleRequired": {
    en: "Your middle name is required for numerology.",
    es: "Tu segundo nombre es obligatorio para la numerología.",
  },
  "name.noMiddle": { en: "I don't have a middle name", es: "No tengo segundo nombre" },
  // OV2-1(a)'s note, which lives in the shared field so a dozen forms cannot drift apart on it. A
  // married name silently entered is not a small error: the numerology is built from those letters.
  "name.atBirthNote": {
    en: "Names AT BIRTH. If a last name changed with marriage, use the maiden name.",
    es: "Nombres AL NACER. Si un apellido cambió con el matrimonio, usa el apellido de soltera.",
  },

  "time.hour": { en: "Hour", es: "Hora" },
  "time.minute": { en: "Minute", es: "Minuto" },
  "time.period": { en: "AM or PM", es: "AM o PM" },
  "time.hourShort": { en: "Hr", es: "Hr" },
  "time.minuteShort": { en: "Min", es: "Min" },

  "place.birthCountry": { en: "Birth country", es: "País de nacimiento" },
  "place.birthCity": { en: "Birthplace city", es: "Ciudad de nacimiento" },
  "place.birthCountryPlaceholder": { en: "Birth country", es: "País de nacimiento" },
  "place.birthCityPlaceholder": { en: "Start typing your birth city", es: "Empieza a escribir tu ciudad de nacimiento" },
  "place.currentCountry": { en: "Country you live in", es: "País donde vives" },
  "place.currentCity": { en: "City you live in", es: "Ciudad donde vives" },
  "place.currentCountryPlaceholder": { en: "Current country", es: "País actual" },
  "place.currentCityPlaceholder": { en: "Start typing your city", es: "Empieza a escribir tu ciudad" },
  "place.countryFirst": { en: "Choose a country first", es: "Elige primero un país" },
  "place.searching": { en: "Searching...", es: "Buscando..." },
  "place.noMatches": { en: "No matches yet. Keep typing.", es: "Todavía no hay resultados. Sigue escribiendo." },
  // R97: the row above is an ANSWER about her town and it was being drawn over a lookup that never
  // answered, on the one field with no coordinates behind it and therefore no reading beyond it.
  // "Keep typing" is the wrong instruction twice over when the geocoder is the thing that is down.
  "place.lookupFailed": {
    en: "That lookup did not answer just then. Try it once more.",
    es: "Esa búsqueda no respondió en ese momento. Inténtalo una vez más.",
  },
  "place.confirmCity": {
    en: "Choose your birth city from the list so your time zone is exact.",
    es: "Elige tu ciudad de nacimiento de la lista para que tu zona horaria sea exacta.",
  },

  // ---- 14-B3: THE MONEY PAGE. Where a Spanish member decides to pay. ----
  //
  // **THE SEAM IS SHARPEST HERE AND IT IS DELIBERATE.** Every tier NAME, price, period, tagline,
  // inheritance band and unlock line arrives from `server/tiers.js` in the payload, and not one of
  // them is in this catalog: the server owns who-gets-what and what it costs (TierCards' own line),
  // the ledger recorded it, and re-translating a price list on the client is a second answer key for
  // the one screen where being wrong costs money. Only the box's OWN words are here.
  //
  // >>> THE POPUP'S OWN COPY IS DELETED HERE, 2026-08-18, ON HER RULING AND ON THIS CATALOG'S OWN
  // LAW (ITEM 14b: **a row nobody reads is a translation no member will ever see**). <<< Her live
  // tester met the retired membership popup at a gated door on production; the structural kill took
  // `PurpleBox` and `TierCards` out of the member-facing bundle, and twenty rows here lost their
  // only reader with them — the eyebrow, the title, the lede, the foot, the catalogue-failure
  // sentence, the pick-to-read label, the checkout-opening line, the two state chips and the four
  // action labels, plus the three frozen HEADINGS. This is the same treatment her membership page's
  // own round gave the eight `onetime.*` rows when it deleted the component that read them (R165).
  //
  // **`tier.frozen.lede` SURVIVES AND IS THE ONE THING THAT HAD TO**, because it is L15's whole
  // member-facing half: a member whose card failed must not be told to CHOOSE a membership she has
  // been paying for all year. It is read by `membershipRefusalKey` in `src/membershipPage.js`, which
  // carries the KEY rather than a second English copy, and the page resolves it.
  //
  // **AND THE CROSSING THAT DIED WITH THEM IS NAMED RATHER THAN LEFT AS AN ABSENCE (R17/R160):**
  // `tier.action.*` was a HELD CROSSING against `tierAction` in `src/lib/tier-state.ts`, which owns
  // whether a button is live and composed the English too. That law has no product consumer left;
  // it is on the next round's R165 list rather than being deleted inside a round about a popup.
  // R73 — WHAT THE BOX SAYS WHERE THE CARDS WOULD HAVE BEEN. A failed catalogue read drew the frame,
  // the eyebrow, "Choose your membership" and NOTHING UNDER IT — R58's absent section on the one
  // screen where an absence costs a sale. It says what did not happen and what to do, and it never
  // says a price, because the prices are what did not arrive.
  // L15 — **THE SAME BOX, DIFFERENT WORDS, WHEN A MEMBERSHIP IS PAUSED RATHER THAN ABSENT.** Her
  // clause is a WARM REACTIVATION SCREEN, and the three lines above it are written for a stranger:
  // *COME ON IN* and *Choose your membership* said to somebody who has been paying all year is a cold
  // wall wearing an upsell. Nothing is deleted here — her rooms, her shelf and her room name are all
  // exactly where she left them, and the lede says so, because that is the thing she is afraid of.
  "tier.frozen.lede": {
    en: "Your card did not go through, so things are on hold for now. Nothing has been deleted and nothing is going anywhere. Sort the payment out and everything opens back up the moment it clears.",
    es: "Tu tarjeta no pasó, así que todo está en pausa por ahora. No se ha borrado nada y nada se va a ninguna parte. Arregla el pago y todo se vuelve a abrir en cuanto se acredite.",
  },

  // WALL-1 — **THE SAME BOX AGAIN, FOR A MEMBER WHO WAS GIVEN HER MEMBERSHIP RATHER THAN CHARGED
  // FOR IT.** `giftTruth` has said *"A gift from the house. Nothing is charged for it."* on her
  // account menu since D2 and reached no other surface, so at a locked door she met the stranger's
  // words. This says what she holds, that nothing is charged for it, and that this particular door
  // sits above it. **It names no tier and no price**, because the ladder underneath is already the
  // page's own job and a sentence naming a tier here would be a second answer to what she holds.
  "tier.gift.lede": {
    en: "Your membership here is a gift from the house, and nothing is charged for it. This one sits a step above what that gift opens. Everything else you have stays exactly where it is.",
    es: "Tu membresía aquí es un regalo de la casa y no se te cobra nada por ella. Esta puerta está un paso por encima de lo que ese regalo abre. Todo lo demás que tienes se queda justo donde está.",
  },

  // R74 — WHAT THE FRONT DOOR SAYS WHEN IT COULD NOT READ ITS OWN WELCOME. A failed read drew no
  // hero at all: no logo band, no title, and no box to ask the first question in. The logo stays
  // (it is a committed asset and always arrives) and this sentence stands where the showcase would
  // have been. It asks for a refresh and promises nothing about what was there.
  "home.showcaseFailed": {
    en: "The welcome could not be loaded just now. Refresh the page, and the rooms below are all open.",
    es: "No se pudo cargar la bienvenida ahora mismo. Recarga la página; las salas de abajo están abiertas.",
  },
  // R74 — the astrocartography line reference. The server has always had an honest sentence for
  // this and it had never reached a member; this room turns, so the words are the catalog's (14-B3).
  "acg.lines.failed": {
    en: "The line reference could not be loaded just now. Your map and your readings are not affected.",
    es: "No se pudo cargar la referencia de líneas ahora mismo. Tu mapa y tus lecturas no se ven afectados.",
  },
  "membership.checkoutFailed": {
    en: "Checkout could not be opened just now. Try again in a moment.",
    es: "No se pudo abrir el pago en este momento. Inténtalo de nuevo en un momento.",
  },
  // A4 — the membership page's value stack. TWO rows and no more: every feature name, every tier
  // label and the "Everything in Newbie" line are the SERVER's own compositions (`tierContents`),
  // exactly as `tierLabel` and `priceText` already are, so this house does not spell what a tier
  // includes a second time (R28/R29). What is new here is a heading and the turns sentence.
  // >>> **`membership.valueHeading` IS DELETED HERE, 2026-08-27 (ITEM 14b's own law).** <<< Her
  // ruling replaced the artwork page with a landing page, the lander draws each tier's value INSIDE
  // its own card, and `MembershipValueStack` — the only thing that ever read this row — is retired
  // with it. **A ROW NOBODY READS IS A TRANSLATION NO MEMBER WILL EVER SEE**, which is the same
  // ruling that deleted the eight `onetime.*` rows one surface over. The lander's own headings are
  // the `membership.lander.*` rows below.
  // The count is a PLACEHOLDER rather than a sentence assembled from fragments (LF-1: Spanish does
  // not put its words where English puts them).
  "membership.valueTurns": { en: "{count} Ask Lala turns a day", es: "{count} turnos con Lala al día" },

  // ===========================================================================================
  // >>> **HER RULING, 2026-09-18 (#271) — THE CANCEL A MEMBER COULD NOT FIND.** <<<
  //
  // *"ADD A CANCEL option to the membership screen (not only upgrade). A member must be able to
  // cancel her own membership."* The ticket came from somebody who had written several times and
  // had nobody answer, and the screen agreed with her.
  //
  // **THE WORDS ARE THE RULING.** The trust-brand standard is the whole point, so:
  //   · The button SAYS cancel. It does not say "manage your plan" with a cancel hidden inside it.
  //   · There is no guilt line, no warning about what she will lose, and no countdown.
  //   · The fine line tells her the one thing she needs: what she has already paid for is hers
  //     until the period ends. That is the truth (the portal cancels at period end), and
  //     saying it plainly is the difference between a cancel she trusts and one she fears.
  // ===========================================================================================
  "membership.manage.lede": {
    en: "Your membership is yours to change or end whenever you like.",
    es: "Tu membresía es tuya para cambiarla o terminarla cuando quieras.",
  },
  "membership.manage.cta": {
    en: "Manage or cancel my membership",
    es: "Gestionar o cancelar mi membresía",
  },
  "membership.manage.busy": {
    en: "Opening…",
    es: "Abriendo…",
  },
  "membership.manage.fine": {
    en: "You keep everything you have already paid for until the end of the period you paid for. Nothing you have made is ever deleted.",
    es: "Conservas todo lo que ya pagaste hasta el final del período pagado. Nada de lo que has creado se elimina nunca.",
  },

  // ===========================================================================================
  // >>> **HER MEMBERSHIP LANDER (2026-08-27). THE PAGE THAT SELLS, IN HER WORDS.** <<<
  //
  // Her ruling: the image-based page is REPLACED because *"it reads as a money grab"*. What
  // replaces it is a real landing page, and these are its words.
  //
  // **NOTHING HERE SPELLS WHAT A TIER INCLUDES.** Every feature name, every price, every tier
  // label and every "Everything in Newbie" line is still the SERVER's own composition, read
  // through `valueStacks` exactly as the old stack read it (R28/R29). What is new is POSITIONING —
  // prose about the house as a whole — and positioning is not a second copy of the ladder.
  //
  // **AND EVERY ROOM NAMED BELOW IS ON THE REAL FEATURE ROSTER** (`server/tiers.js` FEATURES),
  // read off it rather than imagined: a landing page that advertises a room this house does not
  // have is the anti-fabrication law broken on the one surface that asks members for money.
  // ===========================================================================================
  "membership.lander.eyebrow": { en: "The 12th House", es: "La Casa 12" },
  "membership.lander.h1a": { en: "This is not an astrology app.", es: "Esto no es una app de astrología." },
  "membership.lander.h1b": { en: "This is everything.", es: "Esto es todo." },
  "membership.lander.lede": {
    en: "Your chart, your numerology, your cards, your hands, your gifts and your path, all under one roof, with a room full of people already walking it.",
    es: "Tu carta, tu numerología, tus cartas, tus manos, tus dones y tu camino, todo bajo un mismo techo, con una sala llena de gente que ya lo está recorriendo.",
  },
  "membership.lander.heroCta": { en: "See what is inside", es: "Mira lo que hay dentro" },
  "membership.lander.heroNote": {
    en: "Cancel any time from your account settings.",
    es: "Cancela cuando quieras desde los ajustes de tu cuenta.",
  },

  "membership.lander.insideTitle": { en: "Everything you get, one by one", es: "Todo lo que recibes, uno por uno" },
  "membership.lander.insideLede": {
    en: "Most places sell you one of these and charge you for it. Here they are all in the same house, and they were built to be read together.",
    es: "La mayoría de los sitios te venden una sola de estas cosas y te la cobran. Aquí están todas en la misma casa, y fueron hechas para leerse juntas.",
  },

  // ===========================================================================================
  // >>> **HER EIGHTEEN OFFERINGS, ONE BY ONE (2026-08-27, ROUND 2).** <<<
  //
  // Her verdict on the six evocative cards that stood here: **boring**. Her order: *"every
  // offering listed INDIVIDUALLY ... each with a line written to BENEFIT, not description: what it
  // does FOR them, in her register, real-life outcomes, zero feature-speak."*
  //
  // **SO EVERY BODY LINE BELOW IS AN OUTCOME AND NEVER A FEATURE.** Not *the Astrology room* but
  // *finally understand why you are the way you are*; not *compatibility reports* but *before you
  // spend another year guessing*. A line that names a room instead of a result is the feature-speak
  // she rejected, and it is also what the old six were made of.
  //
  // **AND EVERY ONE OF THE EIGHTEEN IS A THING THIS HOUSE REALLY HAS**, checked against the FEATURE
  // roster in `server/tiers.js` and `server/data/videos.json` before it was written — including the
  // masterclasses, which are a real video category (Seals, Angels, Manifestation). A lander
  // advertising a room that does not exist is the anti-fabrication law broken on the surface that
  // asks her members for money.
  //
  // **THE TITLES ARE NOT ROOM NAMES FROM THE ROSTER**, which is the law one gate over: a name that
  // lives in two places is a house a translator can rename by accident.
  // ===========================================================================================
  "membership.lander.o.askLala.title": { en: "Ask Lala", es: "Pregúntale a Lala" },
  "membership.lander.o.askLala.body": {
    en: "Ask her anything. She is already holding your chart.",
    es: "Pregúntale lo que sea. Ya tiene tu carta en la mano.",
  },
  "membership.lander.o.chart.title": { en: "Your birth chart", es: "Tu carta natal" },
  "membership.lander.o.chart.body": {
    en: "The map you were born with, read on real degrees. Finally understand why you are the way you are.",
    es: "El mapa con el que naciste, leído en grados reales. Por fin entiende por qué eres como eres.",
  },
  "membership.lander.o.daily.title": { en: "Daily guidance", es: "Guía diaria" },
  "membership.lander.o.daily.body": {
    en: "Wake up knowing what the day is actually asking of you.",
    es: "Despierta sabiendo lo que el día te está pidiendo de verdad.",
  },
  "membership.lander.o.tarot.title": { en: "Tarot", es: "Tarot" },
  "membership.lander.o.tarot.body": {
    en: "Pull one card when you need an answer now, or sit down for the whole spread.",
    es: "Saca una carta cuando necesites una respuesta ya, o siéntate para la tirada completa.",
  },
  "membership.lander.o.numerology.title": { en: "Your numerology", es: "Tu numerología" },
  "membership.lander.o.numerology.body": {
    en: "Your name and your birth date carry instructions. Read them.",
    es: "Tu nombre y tu fecha de nacimiento llevan instrucciones. Léelas.",
  },
  "membership.lander.o.humanDesign.title": { en: "Your Human Design", es: "Tu Diseño Humano" },
  "membership.lander.o.humanDesign.body": {
    en: "How you are built to decide, so you can stop fighting yourself.",
    es: "Cómo estás hecha para decidir, para que dejes de pelear contigo misma.",
  },
  "membership.lander.o.acg.title": { en: "Your astrocartography", es: "Tu astrocartografía" },
  "membership.lander.o.acg.body": {
    en: "The places on earth where your life gets easier. Know before you move.",
    es: "Los lugares de la tierra donde tu vida se vuelve más fácil. Sábelo antes de mudarte.",
  },
  "membership.lander.o.dreams.title": { en: "Dream interpretation", es: "Interpretación de sueños" },
  "membership.lander.o.dreams.body": {
    en: "The dream that will not leave you alone, finally explained.",
    es: "Ese sueño que no te suelta, por fin explicado.",
  },
  "membership.lander.o.palm.title": { en: "Your palms", es: "Tus manos" },
  "membership.lander.o.palm.body": {
    en: "Your own hands, read line by line.",
    es: "Tus propias manos, leídas línea por línea.",
  },
  "membership.lander.o.compat.title": { en: "Compatibility", es: "Compatibilidad" },
  "membership.lander.o.compat.body": {
    en: "See what is really happening between you two, before you spend another year guessing.",
    es: "Ve lo que de verdad pasa entre ustedes dos, antes de pasar otro año adivinando.",
  },
  "membership.lander.o.seals.title": { en: "Your own seals", es: "Tus propios sellos" },
  "membership.lander.o.seals.body": {
    en: "A seal drawn for your intention and nobody else's, yours to keep.",
    es: "Un sello trazado para tu intención y la de nadie más, tuyo para siempre.",
  },
  "membership.lander.o.magic.title": { en: "The Magic Room", es: "La Sala Mágica" },
  "membership.lander.o.magic.body": {
    en: "The working side of it: spells, oils, herbs and petitions, kept properly.",
    es: "El lado práctico: hechizos, aceites, hierbas y peticiones, guardados como corresponde.",
  },
  "membership.lander.o.library.title": { en: "The library", es: "La biblioteca" },
  "membership.lander.o.library.body": {
    en: "Her whole shelf of books, open to you.",
    es: "Su estantería entera de libros, abierta para ti.",
  },
  "membership.lander.o.masterclasses.title": { en: "The masterclasses", es: "Las clases magistrales" },
  "membership.lander.o.masterclasses.body": {
    en: "Sit down and actually learn it: seals, angels, manifestation.",
    es: "Siéntate y apréndelo de verdad: sellos, ángeles, manifestación.",
  },
  "membership.lander.o.community.title": { en: "The Living Room community", es: "La comunidad de la Sala de Estar" },
  "membership.lander.o.community.body": {
    en: "A room full of people doing the same work, awake at the same hours as you.",
    es: "Una sala llena de gente haciendo el mismo trabajo, despierta a las mismas horas que tú.",
  },
  "membership.lander.o.lives.title": { en: "Lives", es: "Directos" },
  "membership.lander.o.lives.body": {
    en: "Walk into a live while it is happening and ask your question out loud.",
    es: "Entra a un directo mientras ocurre y haz tu pregunta en voz alta.",
  },
  "membership.lander.o.journal.title": { en: "Your journal", es: "Tu diario" },
  "membership.lander.o.journal.body": {
    en: "Somewhere to put it all down that remembers it for you.",
    es: "Un lugar donde dejarlo todo escrito, que lo recuerda por ti.",
  },
  "membership.lander.o.myRoom.title": { en: "My Room", es: "Mi Cuarto" },
  "membership.lander.o.myRoom.body": {
    en: "Your whole practice in one place, waiting exactly as you left it.",
    es: "Toda tu práctica en un solo lugar, esperándote tal como la dejaste.",
  },

  "membership.lander.tiersTitle": { en: "Choose your spiritual level", es: "Elige tu nivel espiritual" },
  "membership.lander.tiersLede": {
    en: "Every tier keeps everything in the tier below it. Nothing is taken away when you move up.",
    es: "Cada nivel conserva todo lo del nivel anterior. Nada se te quita al subir.",
  },
  "membership.lander.perMonth": { en: "a month", es: "al mes" },

  // UPGRADE-NOTE-1 — the half four members in two weeks got wrong. It leads with the RENEWAL,
  // because every one of them expected `pay the difference` to be permanent and was surprised by
  // the next month. It does NOT restate what is due today: Stripe's own screen carries that, and a
  // second spelling of a charge that disagreed by a cent would be worse than no sentence (R25/R73).
  "membership.lander.upgradeNote": {
    en: "You will see exactly what is due today before you pay. From your next renewal this membership is {price} a month.",
    es: "Verás exactamente lo que se cobra hoy antes de pagar. Desde tu próxima renovación, esta membresía cuesta {price} al mes.",
  },


  "membership.lander.reviewsTitle": { en: "What members are saying", es: "Lo que dicen los miembros" },
  "membership.lander.closeCta": { en: "Choose a membership", es: "Elegir una membresía" },
  // >>> THE EIGHT `onetime.*` ROWS ARE DELETED HERE, 2026-08-18 — HER MEMBERSHIP PAGE. <<< Her
  // clause for that page ends **SPIRIT KEEPING ABSENT**, so the card they were written for is
  // retired and its component deleted (R165). ITEM 14b's own law is that **a row nobody reads is a
  // translation no member will ever see**, and it named all eight in the same gate run — so they
  // leave rather than sitting here reading as copy this house still shows somebody. **NOTHING SHE
  // CAN SEE LOST A WORD:** the product's own sentences live on `VideoCoverModal` as `video.getFor`,
  // `video.keep`, `video.openingCheckout` and `video.checkoutFailed`, all four in both languages and
  // all four proved painted by `b1-video-gate-walk.mjs`.

  // ---- 14-B4: MY ROOM. The room she comes back to, and the third surface her plan names. ----
  //
  // **WHAT IS HERS STAYS HERS AND IS NOT IN HERE** — her room name, her journal entries, her saved
  // titles, the people she reads for, the verses she kept. Those are a MEMBER'S OWN WORDS, which is
  // still her open scope question, and this round touched none of them. `room.defaultName` is the one
  // room name in the catalog and it is the house's, not hers: it is what the room is called before she
  // has named it, and the possessive is grammar rather than copy ("El cuarto de Nia", never "Nia's").
  //
  // **AND THE ROTATING CHART FACT IS DELIBERATELY UNTOUCHED, WHICH IS 14-B3's SEAM ONE ROOM OVER.**
  // The first line in that strip is the first sentence of her READING (the engine writes English —
  // `GENERATION_LANGUAGES`), and the rest are built from the vendor's own planet and sign names. A
  // translated frame around English data reads "Tu Sun está en Aries", which is worse than the
  // English sentence it replaced. It moves with part (d), whole, or not at all.
  "common.backHome": { en: "Back to home", es: "Volver al inicio" },
  // ITEM 2 — her membership BACK. It names no destination, because the destination is wherever she
  // came from and a label that named one would be wrong on every arrival but one.
  "common.back": { en: "Back", es: "Atrás" },
  "common.gotIt": { en: "Got it", es: "Entendido" },
  "common.dismiss": { en: "Dismiss", es: "Descartar" },
  "common.close": { en: "Close", es: "Cerrar" },
  "common.cancel": { en: "Cancel", es: "Cancelar" },
  "common.save": { en: "Save", es: "Guardar" },
  // **A SHARED REFUSAL, AND IT IS `common.` BECAUSE FOUR SURFACES SAY IT** — the person editor, the
  // conjure drawers, the notepaper and the cauldron. 14-B6b's back door is this shape one round
  // earlier: a row read by ONE surface while the others hand-type the same sentence is invisible to
  // a check written per surface, so the row is declared shared and all four read it in one round.
  "common.saveFailed": { en: "This could not be saved right now.", es: "Esto no se pudo guardar en este momento." },
  // R56 — **THE COPY CONTROL'S THREE WORDS, AND THERE WAS NO THIRD ONE.** Every Copy button on this
  // site was a two-state label, so "it did not go" was rendered as "Copied" at three of the five
  // sites and as NOTHING AT ALL at the other two. They are `common.` because FIVE surfaces say them
  // (My Room's verse panel, the verse drawer, the conjure drawers, the angel card and Ask Lala) —
  // 14-B6b's back door exactly. `verses.copy` / `verses.copied` / `craft.copy` / `craft.copied` were
  // FOUR rows saying what two rows say, so they are DELETED rather than left beside these (14-B6c's
  // ruling on the reading modals' two duplicate sentences).
  //
  // **THE FAILED WORD NAMES THE ACT RATHER THAN THE CAUSE.** A browser with no Clipboard API and a
  // browser refusing the write are two facts (`unavailable` vs `refused` in `src/lib/clipboard.ts`)
  // and neither is one she can act on; what she can act on is knowing the text is not on her
  // clipboard, so she does not paste the last thing that was.
  "common.copy": { en: "Copy", es: "Copiar" },
  "common.copied": { en: "Copied", es: "Copiado" },
  "common.copyFailed": { en: "Not copied", es: "No se copió" },
  // Ask Lala's control is an ICON, so its whole text is an accessible name — 14-B5's lesson, the one
  // kind of copy nobody looking at the screen can see is still English.
  "common.copyMessage": { en: "Copy message", es: "Copiar el mensaje" },
  // R53 — **THE TWO SENTENCES A TAP THAT OPENED NOTHING NEVER HAD.** Four surfaces ask this route
  // for one stored reading and two of them answered a failure with silence: no modal, no line, no
  // way to tell a broken tap from a slow one. They are `common.` because THREE rooms say them (My
  // Room, the shelf drawer and the astrocartography queue strip), which is 14-B6b's back door
  // avoided rather than repeated — a sentence hand-typed per surface is invisible to a check
  // written per surface. The REFUSED answer arrives with the server's own words instead (a refusal
  // is the only answer that can say why); these two cover the gone session and the lost request.
  "common.openSignIn": { en: "Sign in to open this reading.", es: "Inicia sesión para abrir esta lectura." },
  "common.openFailed": {
    en: "This reading could not be opened right now.",
    es: "Esta lectura no se pudo abrir en este momento.",
  },

  "room.customize": { en: "Customize your room", es: "Personaliza tu cuarto" },
  "room.pointer.customize": { en: "Start here, customize your room", es: "Empieza aquí, personaliza tu cuarto" },
  "room.pointer.shelves": {
    en: "Every reading you run is saved to your Shelves.",
    es: "Cada lectura que hagas se guarda en tus Estantes.",
  },
  "room.pointer.reports": { en: "Your charts are ready for you", es: "Tus cartas ya están listas" },
  "room.defaultName": { en: "{name}'s room", es: "El cuarto de {name}" },
  "room.rename": { en: "Rename your room", es: "Cambia el nombre de tu cuarto" },
  // TRAP-1a: the ribbon's eyebrow. It NAMES WHAT IS BEING OFFERED rather than the machinery
  // (S1-A/R117), and it is an invitation rather than a price -- her own "desire, then explanation,
  // then price" ruling, which begins here.
  // HER OVERNIGHT ITEM 2: the heading over the six doors a paying member came for. It names what
  // they ARE rather than the machinery (S1-A/R117): she asked a question on the front door and this
  // is where the answer lives.
  // >>> HER CORRECTION (2026-08-19) — ONE DOOR, SO THE HEADING IS SINGULAR AND NAMES WHY IT IS
  // THERE. <<< "Your readings" was a plural over a nine-card grid; a plural over one door is the
  // machinery showing through (S1-A/R117: name the thing, never the roster it came from). The
  // retired `roomDoors.heading` and `ribbon.eyebrow` are DELETED at their declarations rather than
  // left unread beside their replacement (DL-2/DT-2b/R181) -- a row nobody reads is a translation no
  // member will ever see, which is ITEM 14b's own census going red.
  "roomDoor.heading": { en: "The reading you came for", es: "La lectura que viniste a buscar" },
  // R51 — the one sentence this room owns about a write that did not land. The other three answers
  // the route can give arrive with the SERVER'S own sentence (a refusal is the only answer that can
  // say why), and a request that never landed falls back to `common.saveFailed`. This one is ours
  // because a gone session is not something the server's words help with: sign in, and it saves.
  "room.saveSignIn": { en: "Sign in to save your room.", es: "Inicia sesión para guardar tu cuarto." },
  // R92: the room has not heard back from the account yet, so there is no row to patch and the
  // change she just made is hers for this session only. It says what happened and what fixes it,
  // and it is true both while the read is in flight and after it dropped.
  "room.saveNotLoaded": { en: "Your room has not finished loading, so that change was not saved. Reload and try it again.", es: "Tu cuarto no terminó de cargar, así que ese cambio no se guardó. Recarga la página e inténtalo de nuevo." },
  "room.editDetails": { en: "Edit your name and birth details", es: "Edita tu nombre y tus datos de nacimiento" },
  "room.lifePath": { en: "Life Path {number}", es: "Camino de Vida {number}" },
  // IT #326: her room's read did not land. Not "create your room": nothing of hers is gone.
  "room.unreachable": {
    en: "Your room could not be reached just now. Nothing is lost: your chart, your readings and everything you have kept are safe.",
    es: "No pudimos abrir tu cuarto en este momento. No se perdió nada: tu carta, tus lecturas y todo lo que has guardado están a salvo.",
  },
  "room.tryAgain": { en: "Try again", es: "Intentar de nuevo" },
  "room.noChartYet": {
    en: "Create your room from the home page to fill this space with your chart.",
    es: "Crea tu cuarto desde la página de inicio para llenar este espacio con tu carta.",
  },

  // N19-1's one-time ask, for members who signed up before the current-location step existed.
  "room.location.ask": {
    en: "Your transits and your daily guidance are running on your birthplace. Tell me where you live now and they will run on your own transits and your own clock. Your birth chart never changes.",
    es: "Tus tránsitos y tu guía diaria se están calculando desde tu lugar de nacimiento. Dime dónde vives ahora y se calcularán con tus propios tránsitos y tu propio reloj. Tu carta natal nunca cambia.",
  },
  "room.location.go": { en: "Where I live now", es: "Dónde vivo ahora" },
  "room.location.not": { en: "Not now", es: "Ahora no" },

  // The details editor, which is the shared signup form opened over her room.
  "details.label": { en: "Edit your details", es: "Edita tus datos" },
  "details.eyebrow": { en: "Your details", es: "Tus datos" },
  "details.title": { en: "Edit your information", es: "Edita tu información" },
  "details.lede": {
    en: "Change what you signed up with. Every reading you run from here on uses the corrected details. The readings you already have stay exactly as they are.",
    es: "Cambia lo que pusiste al registrarte. Cada lectura que hagas de aquí en adelante usa los datos corregidos. Las lecturas que ya tienes se quedan exactamente como están.",
  },
  "details.submit": { en: "Save my details", es: "Guardar mis datos" },
  "details.saving": { en: "Saving...", es: "Guardando..." },
  "details.recasting": {
    en: "Saving your details and recasting your chart...",
    es: "Guardando tus datos y volviendo a calcular tu carta...",
  },
  "details.saved": { en: "Your details are saved.", es: "Tus datos están guardados." },
  "details.failed": {
    en: "Your details could not be saved right now.",
    es: "Tus datos no se pudieron guardar en este momento.",
  },

  // The nine panels her room is built from. `RoomPanel` composes the toggle's accessible name from
  // the title, which is the only sentence that component writes itself — and a screen-reader-only
  // name is exactly the kind that stays English forever with nobody able to see it.
  "panel.expand": { en: "Expand {title}", es: "Abrir {title}" },
  "panel.minimize": { en: "Minimize {title}", es: "Minimizar {title}" },
  "panel.reports.eyebrow": { en: "Your reports", es: "Tus informes" },
  "panel.reports.title": { en: "Your Reports", es: "Tus Informes" },
  "panel.daily.eyebrow": { en: "Today", es: "Hoy" },
  "panel.daily.title": { en: "Daily guidance", es: "Guía diaria" },
  "panel.tarot.eyebrow": { en: "Tarot", es: "Tarot" },
  "panel.tarot.title": { en: "Your cards", es: "Tus cartas" },
  "panel.shelf.eyebrow": { en: "Bookshelf", es: "Librero" },
  "panel.shelf.title": { en: "Saved shelf", es: "Estante guardado" },
  "panel.bookmarks.eyebrow": { en: "Your marks", es: "Tus marcas" },
  "panel.bookmarks.title": { en: "Bookmarks", es: "Marcadores" },
  "panel.verses.eyebrow": { en: "Scripture", es: "Escritura" },
  "panel.verses.title": { en: "Saved Verses", es: "Versículos guardados" },
  "panel.journal.eyebrow": { en: "Your notes", es: "Tus notas" },
  "panel.journal.title": { en: "Journal", es: "Diario" },
  "panel.people.eyebrow": { en: "Your circle", es: "Tu círculo" },
  "panel.people.title": { en: "People of Interest", es: "Personas de interés" },
  "panel.videos.eyebrow": { en: "Videos", es: "Videos" },
  "panel.videos.title": { en: "Continue watching", es: "Seguir viendo" },

  // Your Reports. The report NAMES are not here (`REPORT_TYPE_LABELS`, R32, and the server agree on
  // what a reading is called); these are the sentences the house writes AROUND them.
  "reports.noProfile": {
    en: "Create your room to generate your chart, numerology, and compatibility profile.",
    es: "Crea tu cuarto para generar tu carta, tu numerología y tu perfil de compatibilidad.",
  },
  "reports.preparing": { en: "still being prepared", es: "todavía se está preparando" },
  "reports.writingYours": { en: "Writing yours now", es: "Escribiendo la tuya ahora" },
  "reports.writeMine": { en: "Write mine", es: "Escribe la mía" },
  // CE-1 (2026-08-09): `reports.open` ("Open") and `reports.readyToOpen` ("Ready to open") are
  // DELETED rather than left standing beside these two (DL-2/DT-2b/R181). They were the copy of the
  // ready lie: both were rendered on the branch reached BECAUSE the report row is missing, so a
  // member with no birth chart read "Ready to open" over an empty slot. Deleting them at the
  // declaration is what makes the sentence unrenderable rather than merely unrouted.
  "reports.writeItNow": { en: "Write it now", es: "Escríbela ahora" },
  "reports.didNotArrive": { en: "This one did not arrive", es: "Esta no llegó" },
  "reports.notTemplated": {
    en: "This one is written for you, not templated",
    es: "Esta se escribe para ti, no es una plantilla",
  },
  "reports.freeOnTap": { en: "Free, written when you tap", es: "Gratis, se escribe cuando la pidas" },
  "reports.working": { en: "Still working", es: "Todavía trabajando" },
  "reports.generate": { en: "Generate", es: "Generar" },
  "reports.failed": {
    en: "That reading could not be generated right now. Try again in a moment.",
    es: "Esa lectura no se pudo generar en este momento. Inténtalo de nuevo en un momento.",
  },
  "reports.download": { en: "Download PDF", es: "Descargar PDF" },
  "reports.closeReport": { en: "Close report", es: "Cerrar el informe" },

  "daily.empty": {
    en: "Your guidance for today, drawn from your chart and the day's transits.",
    es: "Tu guía para hoy, sacada de tu carta y de los tránsitos del día.",
  },
  "daily.get": { en: "Get today's guidance", es: "Recibe la guía de hoy" },
  // R75 — WHAT THE PANEL SAYS WHEN THE TAP DID NOT LAND. Every refusal on this route was swallowed,
  // so the button said "Reading your transits...", stopped saying it, and nothing happened — on the
  // one control in this house whose every tap costs two vendor calls and an engine call. `signIn` is
  // kept apart because it is the one she fixes in a single tap (R61); the rest end in "try again".
  "daily.failed": {
    en: "Today's guidance could not be prepared right now. Please try again in a moment.",
    es: "La guía de hoy no se pudo preparar ahora mismo. Vuelve a intentarlo en un momento.",
  },
  "daily.signIn": {
    en: "Your session has ended. Sign in again for today's guidance.",
    es: "Tu sesión ha terminado. Inicia sesión de nuevo para la guía de hoy.",
  },
  "daily.reading": { en: "Reading your transits...", es: "Leyendo tus tránsitos..." },
  "daily.noProfile": {
    en: "Create your room for daily guidance shaped to your chart.",
    es: "Crea tu cuarto para recibir una guía diaria hecha a la medida de tu carta.",
  },

  "tarot.empty": { en: "No readings yet. Ask the cards a question.", es: "Todavía no hay lecturas. Hazle una pregunta a las cartas." },
  "tarot.drawDaily": { en: "Draw your daily card", es: "Saca tu carta del día" },
  "tarot.openRoom": { en: "Open the tarot room for spreads", es: "Abre el cuarto del tarot para tiradas" },

  "shelf.empty": { en: "Books you save from the reader collect here.", es: "Los libros que guardes desde el lector se juntan aquí." },
  "shelf.openLibrary": { en: "Open library", es: "Abre la biblioteca" },

  "bookmarks.empty": {
    en: "No bookmarks yet. Name a page in any reader and it lands here.",
    es: "Todavía no hay marcadores. Ponle nombre a una página en cualquier lector y aterriza aquí.",
  },
  "bookmarks.remove": { en: "Remove {name}", es: "Quitar {name}" },
  // M3 (her words, 2026-10-20): a bookmark whose book has left the library.
  "bookmarks.gone": { en: "This book is no longer in the library", es: "Este libro ya no está en la biblioteca" },

  "verses.empty": {
    en: "No saved verses yet. Open Verses on the right edge, search a topic, and save the ones that speak to you.",
    es: "Todavía no hay versículos guardados. Abre Versículos en el borde derecho, busca un tema y guarda los que te hablen.",
  },
  "verses.remove": { en: "Remove", es: "Quitar" },
  "verses.removeAria": { en: "Remove {reference}", es: "Quitar {reference}" },
  // R54 — the three sentences a pin that did not stick never had. The chip used to read "✓ Saved"
  // over all four of this route's refusals, and the unpin took the verse off her screen without
  // reading whether a row went. `failed` differs between saving and unpinning, so they are two rows
  // rather than one shared vague one; the REFUSED answer arrives with the server's own words.
  "verses.signIn": { en: "Sign in to keep verses in your room.", es: "Inicia sesión para guardar versículos en tu cuarto." },
  "verses.saveFailed": {
    en: "This verse could not be saved right now.",
    es: "Este versículo no se pudo guardar en este momento.",
  },
  "verses.removeFailed": {
    en: "This verse could not be removed right now.",
    es: "Este versículo no se pudo quitar en este momento.",
  },

  "journal.empty": {
    en: "No entries yet. Open the Journal on the right edge and write your first note.",
    es: "Todavía no hay notas. Abre el Diario en el borde derecho y escribe tu primera nota.",
  },
  // R57 — the sentences her journal never had. `loadFailed` is T8-9's distinction one panel over:
  // `journal.empty` above states as FACT that she has written nothing, which a failed read has no
  // way of knowing. `saveFailed` is the one that matters most on this site, because there is no
  // shelf to check afterwards — it says what happened AND what is still true of the words on screen,
  // and `journal.unsaved` marks the row itself so the two never come apart.
  "journal.loadFailed": {
    en: "Your entries could not be loaded just now. Refresh and they will come back.",
    es: "Tus notas no se pudieron cargar ahora mismo. Actualiza y volverán.",
  },
  "journal.signIn": { en: "Sign in to keep this entry in your room.", es: "Inicia sesión para guardar esta nota en tu cuarto." },
  "journal.saveFailed": {
    en: "This entry was not saved to your room. It is here until you close this tab.",
    es: "Esta nota no se guardó en tu cuarto. Estará aquí hasta que cierres esta pestaña.",
  },
  "journal.unsaved": { en: "Not saved", es: "Sin guardar" },
  // R58 — the sentence a room says when its own offerings did not arrive. Every button grid in the
  // astrology and numerology rooms is `configs.length ? <section> : null`, so a failed read is not
  // an empty grid, it is a band of the room that is simply not there. ONE row for both rooms, and
  // deliberately not a per-menu sentence: four red lines in one room is noise, and they fail
  // together in practice.
  "menus.loadFailed": {
    en: "Some of this room's readings could not be loaded just now. Refresh and they will come back.",
    es: "Algunas lecturas de este cuarto no se pudieron cargar ahora mismo. Actualiza y volverán.",
  },

  "people.empty": {
    en: "Save the people you read for, then run their chart or a compatibility with one click.",
    es: "Guarda a las personas para las que lees, y luego haz su carta o una compatibilidad con un clic.",
  },
  "people.noBirthDate": { en: "no birth date", es: "sin fecha de nacimiento" },
  "people.addDetails": { en: "Add birth details", es: "Agrega los datos de nacimiento" },
  "people.needsDetails": {
    en: "{name} needs a birth time and birthplace before a chart can be cast",
    es: "{name} necesita una hora y un lugar de nacimiento antes de poder calcular una carta",
  },
  "people.opening": { en: "Opening", es: "Abriendo" },
  "people.running": { en: "Running", es: "Calculando" },
  "people.viewChart": { en: "View Birth Chart", es: "Ver la carta natal" },
  "people.runChart": { en: "Run Birth Chart", es: "Hacer la carta natal" },
  "people.reading": { en: "Reading", es: "Leyendo" },
  "people.readReport": { en: "Read Report", es: "Leer el informe" },
  "people.compatibility": { en: "Compatibility", es: "Compatibilidad" },
  "people.edit": { en: "Edit", es: "Editar" },
  "people.add": { en: "Add a person", es: "Agrega una persona" },
  "people.relationship": { en: "Relationship (optional)", es: "Relación (opcional)" },
  "people.relationshipPlaceholder": { en: "partner, friend, family...", es: "pareja, amistad, familia..." },
  "people.saving": { en: "Saving", es: "Guardando" },
  "people.savePerson": { en: "Save person", es: "Guardar persona" },
  "people.signIn": { en: "Sign in to run this chart.", es: "Inicia sesión para hacer esta carta." },
  // R52 — the one sentence this form owns about a save that did not land. Every other answer arrives
  // with the SERVER'S own words (the only ones that can say why), a duplicate raises Rule 3's prompt
  // instead of a sentence, and a request that never landed falls back to `common.saveFailed`.
  "people.saveSignIn": { en: "Sign in to save people to your room.", es: "Inicia sesión para guardar personas en tu cuarto." },
  "people.chartFailed": {
    en: "{name}'s chart could not be prepared right now. Please try again.",
    es: "La carta de {name} no se pudo preparar en este momento. Inténtalo de nuevo.",
  },

  "videos.empty": {
    en: "Start a class in the Video Library and it will wait for you here, right where you left off.",
    es: "Empieza una clase en la Videoteca y te espera aquí, justo donde la dejaste.",
  },
  "videos.finished": { en: "Finished", es: "Terminado" },
  "videos.watched": { en: "{percent}% watched", es: "{percent}% visto" },
  "videos.notStarted": { en: "Not started", es: "Sin empezar" },
  "videos.again": { en: "Watch again", es: "Ver de nuevo" },
  "videos.resume": { en: "Resume", es: "Continuar" },
  "videos.remove": { en: "Remove {title} from Continue watching", es: "Quitar {title} de Seguir viendo" },
  // R55 — the sentences Continue watching never had. `loadFailed` is T8-9's distinction one panel
  // over: a shelf that ARRIVED EMPTY and a shelf that DID NOT ARRIVE are not the same sentence, and
  // `videos.empty` above states as fact ("start a class and it will wait for you here") something a
  // failed read has no way of knowing. `removeFailed` is what puts the row back after the optimistic
  // paint; the REFUSED answer arrives with the server's own words instead of either of these.
  "videos.loadFailed": {
    en: "Continue watching could not be loaded just now. Refresh and it will come back.",
    es: "Seguir viendo no se pudo cargar ahora mismo. Actualiza y volverá.",
  },
  "videos.signIn": { en: "Sign in to keep your place in a video.", es: "Inicia sesión para guardar tu lugar en un video." },
  "videos.removeFailed": {
    en: "This video could not be removed from Continue watching right now.",
    es: "Este video no se pudo quitar de Seguir viendo en este momento.",
  },

  // The customize modal, which is where her theme lives too.
  "customize.eyebrow": { en: "Your space", es: "Tu espacio" },
  "customize.backgrounds": {
    en: "Pick a background and your room transforms behind you. Tap the selected one again to clear it.",
    es: "Elige un fondo y tu cuarto se transforma detrás de ti. Toca otra vez el que elegiste para quitarlo.",
  },
  "customize.choose": { en: "Choose {name}", es: "Elige {name}" },
  // R60 — the sentence the picker says when its own thumbnails did not arrive. Without it an empty
  // grid is indistinguishable from a house that ships no backgrounds at all.
  "customize.backgroundsFailed": {
    en: "The backgrounds could not be loaded just now. Refresh and they will come back.",
    es: "Los fondos no se pudieron cargar ahora mismo. Actualiza y volverán.",
  },

  // ---- 14-B5: THE CHROME. What is on every screen both earlier rounds photographed. ----
  //
  // **THE LINE THIS ROUND DRAWS IS BETWEEN A DOOR AND A NAME, AND IT IS A BICONDITIONAL RATHER THAN
  // A JUDGEMENT CALL.** `homeMenuItems` (src/catalogs/rooms.tsx) is the one roster that NAMES this
  // house's rooms — Magic Room, Ask Lala Room, The Library, Living Room — and those names are the
  // house's own, declared ENGLISH BY DESIGN below with their reason. The two entries in the mobile
  // sheet the roster does NOT name are `home` and `my-room`: they are generic doors, not rooms with
  // names, and they turn. So "which of these words is a brand" stops being asked per label — it is
  // answered by whether the room roster carries the page, and the gate holds exactly that.
  //
  // `account.myRoom` is REUSED for My Room rather than copied: the account menu settled "Mi Cuarto"
  // in 14(b), and a second row would be a second answer to the same question one tab bar away.
  "nav.primary": { en: "Primary", es: "Principal" },
  "nav.siteInstructions": { en: "Site Instructions", es: "Instrucciones del sitio" },
  "nav.menu": { en: "Menu", es: "Menú" },
  "nav.closeMenu": { en: "Close menu", es: "Cerrar el menú" },
  "nav.home": { en: "Home", es: "Inicio" },
  "nav.rooms": { en: "Rooms", es: "Cuartos" },
  "nav.closeRooms": { en: "Close rooms", es: "Cerrar los cuartos" },
  "nav.appNavigation": { en: "App navigation", es: "Navegación de la aplicación" },
  // The bell and the mail. Two rows each, because "3 new messages" and "Messages" are two different
  // sentences and a count folded into one row with an optional token would render "{count}" at her
  // when there is nothing new (the unfilled-token law, working exactly as designed and in the wrong
  // place). Spanish puts the adjective after the noun, which is why parity is a token law and not a
  // string-concatenation one.
  "nav.notifications": { en: "Notifications", es: "Notificaciones" },
  "nav.notificationsUnread": { en: "{count} new notifications", es: "{count} notificaciones nuevas" },
  "nav.messages": { en: "Messages", es: "Mensajes" },
  "nav.messagesUnread": { en: "{count} new messages", es: "{count} mensajes nuevos" },

  // ---- 14-B6a: THE DELIVERY BLOCK. What every finished reading on this site says about itself. ----
  //
  // R24 pulled these sentences into `src/components/ResultDestinations.tsx`; NINETEEN surfaces render
  // that block and EIGHTEEN of them kept a hand-typed copy, so translating the component alone would
  // have turned one room in nineteen. The derivation came first and the rows came second, which is why
  // there are five of them here rather than ninety.
  //
  // `{room}` is filled by `roomLabel`, which resolves the names `src/catalogs/rooms.tsx` publishes —
  // and that roster is `brand.`, ENGLISH BY DESIGN. So this row reads "En tu estante de Astrology",
  // which is 14-B3's money-page seam one room over and is the deliberate one, not a gap.
  //
  // Download PDF is NOT here: `reports.download` already says it (14-B4) and a second row would be a
  // second answer to one question, which is the whole disease this catalog's gate exists to catch.
  "result.onShelf": { en: "On your {room} shelf", es: "En tu estante de {room}" },
  "result.save": { en: "Save to My Room", es: "Guardar en Mi Cuarto" },
  "result.saving": { en: "Saving…", es: "Guardando…" },
  "result.saved": { en: "✓ Saved to My Room", es: "✓ Guardado en Mi Cuarto" },
  "result.noHostage": {
    en: "Feel free to move around while this comes through. If you step away, it will be waiting for you on your {room} shelf.",
    es: "Puedes moverte por la casa mientras esto llega. Si te vas, te va a estar esperando en tu estante de {room}.",
  },

  // ---- 14-B6b: THE FOUR SMALLER READING ROOMS. Dreams, Palmistry, Numerology, Human Design. ----
  //
  // **THE SEAM IS 14-B3's, THIRD TIME, AND IT IS THE WHOLE DESIGN OF THESE ROWS:** a room's own words
  // turn and THE DELIVERED READING DOES NOT (`GENERATION_LANGUAGES = ["en"]`, ITEM 14d). So a Spanish
  // room around an English reading is what ships until she rules on (d), and it is deliberate rather
  // than half-done. Everything here is the house talking to her BEFORE the engine is called: the
  // invitation, the form's own words, the waiting lines, the refusals.
  //
  // **THE EYEBROW OF EACH ROOM IS NOT HERE, AND THAT IS 14-B5's BICONDITIONAL, NOT AN OVERSIGHT.**
  // "Dream Interpretation", "Palmistry", "Numerology room", "Human Design room" all print the name
  // `src/catalogs/rooms.tsx` publishes, and that roster is `brand.` — declared ENGLISH BY DESIGN,
  // because naming her rooms is a branding act. The gate holds each eyebrow against the roster BY
  // IMPORT, so the two cannot drift and nobody can quietly translate one.
  //
  // The three save sentences are SHARED, not written once per room: every room saves to the same
  // shelf through the same route, and four copies of one sentence is four chances to drift.
  "save.signIn": {
    en: "Sign in to save this to your room.",
    es: "Inicia sesión para guardar esto en tu cuarto.",
  },
  "save.failed": {
    en: "This could not be saved right now.",
    es: "Esto no se pudo guardar en este momento.",
  },
  "save.done": {
    en: "Saved. Find it under Reports in My Room.",
    es: "Guardado. Lo encuentras en Tus Informes, en Mi Cuarto.",
  },

  // The dream room. The mic labels are ACCESSIBLE NAMES as well as tooltips, which is the kind that
  // stays English forever because nobody looking at the screen can see them (14-B4's lesson).
  "dream.title": { en: "Have your dream read", es: "Que te lean tu sueño" },
  "dream.lede": {
    en: "Tell Lala your dream, in your own words. She reads it the old way, the symbols, the colors, the feeling, the ancestors and signs in it, and the message it carries. Type it, or press the mic and speak it.",
    es: "Cuéntale tu sueño a Lala, con tus propias palabras. Ella lo lee a la antigua: los símbolos, los colores, lo que sentiste, los ancestros y las señales que trae, y el mensaje que carga. Escríbelo, o toca el micrófono y cuéntalo en voz alta.",
  },
  "dream.placeholder": {
    en: "I dreamed I was standing at my grandmother's door, and the water kept rising...",
    es: "Soñé que estaba parada en la puerta de mi abuela, y el agua seguía subiendo...",
  },
  "dream.micStart": { en: "Speak your dream", es: "Cuenta tu sueño en voz alta" },
  "dream.micStop": { en: "Stop dictating", es: "Deja de dictar" },
  "dream.listening": {
    en: "Listening... speak your dream, press the mic again to stop.",
    es: "Escuchando... cuenta tu sueño, toca el micrófono otra vez para parar.",
  },
  // IT #345: said when the mic stopped on its own, so a silent mic is never the whole answer.
  "dream.micRefused": {
    en: "Your phone didn't let the mic in. Allow the microphone for 12th House in your phone's settings, then tap the mic again, or type your dream.",
    es: "Tu teléfono no dejó entrar el micrófono. Permite el micrófono para 12th House en los ajustes de tu teléfono y toca el micrófono otra vez, o escribe tu sueño.",
  },
  "dream.micUnavailable": {
    en: "The mic couldn't start just now. Tap it to try again, or type your dream.",
    es: "El micrófono no pudo empezar ahora mismo. Tócalo para intentarlo otra vez, o escribe tu sueño.",
  },
  "dream.needMore": {
    en: "Tell me the dream first, in as much detail as you remember.",
    es: "Cuéntame primero el sueño, con todo el detalle que recuerdes.",
  },
  "dream.go": { en: "Read my dream", es: "Lee mi sueño" },
  "dream.going": { en: "Reading your dream...", es: "Leyendo tu sueño..." },
  "dream.stage1": { en: "Sitting with your dream...", es: "Sentándome con tu sueño..." },
  "dream.stage2": { en: "Reading the symbols and colors...", es: "Leyendo los símbolos y los colores..." },
  "dream.stage3": { en: "Listening for ancestors and messages...", es: "Escuchando a los ancestros y sus mensajes..." },
  "dream.stage4": { en: "Writing your reading...", es: "Escribiendo tu lectura..." },
  "dream.roomSignIn": { en: "Sign in to have your dream read.", es: "Inicia sesión para que te lean tu sueño." },
  "dream.failed": {
    en: "Your dream reading could not be prepared right now. Please try again.",
    es: "Tu lectura de sueño no se pudo preparar en este momento. Inténtalo de nuevo.",
  },

  // The palmistry room. The photo refusals are the ones that matter most: a member who cannot read
  // why her picture was turned away goes and takes the same picture again.
  "palm.title": { en: "Have your palm read", es: "Que te lean la mano" },
  "palm.lede": {
    en: "Take a clear, well-lit photo of your open palm, fingers spread, and Lala reads it the old way: your heart, head, and life lines, your mounts, your hand shape, and the markings that carry your story. She reads only what your hand truly shows.",
    es: "Tómale una foto clara y bien iluminada a tu mano abierta, con los dedos separados, y Lala la lee a la antigua: tus líneas del corazón, de la cabeza y de la vida, tus montes, la forma de tu mano, y las marcas que cargan tu historia. Ella lee solo lo que tu mano de verdad muestra.",
  },
  "palm.notImage": {
    en: "Please choose a photo of your palm (a JPEG or PNG).",
    es: "Elige una foto de tu mano, por favor (un JPEG o un PNG).",
  },
  "palm.preparing": { en: "Getting your photo ready...", es: "Preparando tu foto..." },
  "palm.photoFailed": {
    en: "That photo did not come through. Please choose it again.",
    es: "Esa foto no llegó completa. Vuelve a elegirla, por favor.",
  },
  "palm.needPhoto": {
    en: "Add a clear photo of your open palm first.",
    es: "Primero agrega una foto clara de tu mano abierta.",
  },
  "palm.add": { en: "Add a photo of your open palm", es: "Agrega una foto de tu mano abierta" },
  "palm.retake": { en: "Choose a different photo", es: "Elige otra foto" },
  "palm.alt": { en: "Your palm", es: "Tu mano" },
  "palm.go": { en: "Read my palm", es: "Lee mi mano" },
  "palm.going": { en: "Reading your palm...", es: "Leyendo tu mano..." },
  "palm.stage1": { en: "Looking at your hand...", es: "Mirando tu mano..." },
  "palm.stage2": { en: "Tracing the lines...", es: "Siguiendo las líneas..." },
  "palm.stage3": { en: "Reading the mounts and markings...", es: "Leyendo los montes y las marcas..." },
  "palm.stage4": { en: "Writing your reading...", es: "Escribiendo tu lectura..." },
  "palm.roomSignIn": { en: "Sign in to have your palm read.", es: "Inicia sesión para que te lean la mano." },
  // R210: `palm.unclear` was DELETED at its declaration (DL-2/DT-2b/R181), not merely unrendered.
  // `handlePalmReading` composes that sentence itself, byte for byte, on the one arm that has
  // actually looked at her hand — so the client's copy was a second answer key (R28/R29), and the
  // only case it could ever reach was the case the server had NOT spoken in: a house fault, where
  // it is a false accusation about her photograph. **The Spanish half is a real loss and it is
  // NAMED rather than hidden (R17/R160): the server's own copy is English-only, so a Spanish member
  // whose palm truly is unclear reads English today. Giving the server this catalog is its own
  // round** — it needs a member language this route does not resolve (DECISIONS R210-3).
  "palm.failed": {
    en: "Your palm reading could not be prepared right now. Please try again.",
    es: "Tu lectura de la mano no se pudo preparar en este momento. Inténtalo de nuevo.",
  },

  // The numerology room. `form.birthDate` is REUSED for the date field rather than written again.
  "numerology.title": { en: "Your Mathematical Blueprint", es: "Tu plano matemático" },
  "numerology.lede": {
    en: "Numerology is the old art of reading the numbers hidden in your full name and birth date. Each one carries a real piece of you, your path, your gifts, what your heart is after, and Lala reads them straight, the way they show up in your life.",
    es: "La numerología es el arte antiguo de leer los números escondidos en tu nombre completo y tu fecha de nacimiento. Cada uno carga una parte real de ti: tu camino, tus dones, lo que tu corazón anda buscando, y Lala los lee directo, tal como aparecen en tu vida.",
  },
  "numerology.nameHint": {
    en: "Your full name at birth, including your middle name, so your numerology is accurate.",
    es: "Tu nombre completo al nacer, con tu segundo nombre, para que tu numerología sea exacta.",
  },
  "numerology.go": { en: "Read my numerology", es: "Lee mi numerología" },
  "numerology.going": { en: "Reading your numerology...", es: "Leyendo tu numerología..." },
  "numerology.oneNumber": { en: "Or read one number in depth:", es: "O lee un solo número a fondo:" },
  "numerology.minis": { en: "Numerology mini readings", es: "Lecturas cortas de numerología" },
  "numerology.roomSignIn": {
    en: "Sign in to run your numerology reading.",
    es: "Inicia sesión para hacer tu lectura de numerología.",
  },
  // (the numerology room's failure sentence is `reading.failed` — 14-B6c found that the SAME
  // sentence was hand-typed in four reading modals beside this row, so there is one row now)

  // The Human Design room, and the compatibility half beside it. The two halves are separate rows
  // throughout: they are two readings, two refusals and two sets of waiting lines, and folding them
  // together is how a member reading one is told about the other.
  "hd.title": { en: "Decode Your Energetic Design", es: "Descifra tu diseño energético" },
  "hd.lede": {
    en: "Human Design is the map of how your energy is actually wired: your type and the strategy that keeps your life clean, the inner authority you decide from, your profile, and the centers, channels, and gates that make you you. Enter your birth details and Lala reads your bodygraph straight.",
    es: "El Diseño Humano es el mapa de cómo está cableada tu energía de verdad: tu tipo y la estrategia que te mantiene la vida limpia, la autoridad interna desde la que decides, tu perfil, y los centros, canales y puertas que te hacen ser tú. Pon tus datos de nacimiento y Lala te lee el bodygraph directo.",
  },
  "hd.nameHint": {
    en: "Full name at birth, including your middle name, so your Human Design is accurate.",
    es: "Nombre completo al nacer, con tu segundo nombre, para que tu Diseño Humano sea exacto.",
  },
  "hd.go": { en: "Decode my design", es: "Descifra mi diseño" },
  // The busy label on the button IS the first waiting line, so it is ONE row read twice rather than
  // two rows that happen to agree today. Two rows saying one thing is this catalog's own disease.
  "hd.stage1": { en: "Casting your bodygraph...", es: "Calculando tu bodygraph..." },
  "hd.stage2": { en: "Reading your type and authority...", es: "Leyendo tu tipo y tu autoridad..." },
  "hd.stage3": { en: "Mapping your centers and channels...", es: "Trazando tus centros y tus canales..." },
  "hd.stage4": { en: "Writing your design...", es: "Escribiendo tu diseño..." },
  "hd.roomSignIn": {
    en: "Sign in to decode your Human Design.",
    es: "Inicia sesión para descifrar tu Diseño Humano.",
  },
  "hd.needsBirth": {
    en: "Your Human Design needs your birth date, birth time, and a birthplace with coordinates.",
    es: "Tu Diseño Humano necesita tu fecha de nacimiento, tu hora y un lugar de nacimiento con coordenadas.",
  },
  "hd.failed": {
    en: "Your bodygraph could not be prepared right now. Please try again.",
    es: "Tu bodygraph no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "hd.compat.eyebrow": { en: "Relationship compatibility", es: "Compatibilidad de relación" },
  "hd.compat.title": { en: "Read your design with someone", es: "Lee tu diseño junto al de alguien" },
  "hd.compat.lede": {
    en: "Your own bodygraph above is one half. Pick someone from your circle or add their birth details, and Lala reads how your two designs actually meet: your composite channels, the electromagnetic pull, and where you compromise.",
    es: "Tu propio bodygraph de arriba es una mitad. Elige a alguien de tu círculo o pon sus datos de nacimiento, y Lala lee cómo se encuentran de verdad los dos diseños: sus canales compuestos, la atracción electromagnética, y dónde ceden.",
  },
  "hd.compat.picker": { en: "Who are you reading with?", es: "¿Con quién estás leyendo?" },
  "hd.compat.nameHint": {
    en: "Their full name at birth, plus birth date, time, and birthplace, so their Human Design is accurate.",
    es: "Su nombre completo al nacer, más su fecha, su hora y su lugar de nacimiento, para que su Diseño Humano sea exacto.",
  },
  "hd.compat.go": { en: "Read our compatibility", es: "Lee nuestra compatibilidad" },
  "hd.compat.going": { en: "Reading your compatibility...", es: "Leyendo su compatibilidad..." },
  "hd.compat.needPartner": {
    en: "Add your partner's name and birth details to read your compatibility.",
    es: "Pon el nombre y los datos de nacimiento de tu pareja para leer su compatibilidad.",
  },
  "hd.compat.stage1": { en: "Casting both bodygraphs...", es: "Calculando los dos bodygraphs..." },
  "hd.compat.stage2": { en: "Reading how your energies meet...", es: "Leyendo cómo se encuentran sus energías..." },
  "hd.compat.stage3": { en: "Mapping your composite channels...", es: "Trazando sus canales compuestos..." },
  "hd.compat.stage4": { en: "Writing your connection...", es: "Escribiendo su conexión..." },
  "hd.compat.roomSignIn": {
    en: "Sign in to read your Human Design compatibility.",
    es: "Inicia sesión para leer su compatibilidad de Diseño Humano.",
  },
  "hd.compat.needsBirth": {
    en: "Both charts need a birth date, birth time, and a birthplace with coordinates.",
    es: "Las dos cartas necesitan fecha de nacimiento, hora y un lugar de nacimiento con coordenadas.",
  },
  "hd.compat.failed": {
    en: "Your compatibility could not be prepared right now. Please try again.",
    es: "Su compatibilidad no se pudo preparar en este momento. Inténtalo de nuevo.",
  },

  // ---- 14-B6c: THE SUBJECT PICKER, WHICH IS THE FIRST CONTROL IN EVERY READING ROOM ----------
  //
  // **THIS IS THE MOST VISIBLE ENGLISH LEFT ON A SPANISH READING ROOM**, photographed in
  // `outputs/item14/13-human-design-spanish-393.png` and deliberately not cropped out: the room's
  // heading, lede and button turned in 14-B6b and the picker directly beneath them did not.
  //
  // **THE ROWS ARE THE COMPONENT'S DEFAULTS, NEVER A TRANSLATION OVER A CALL SITE** — R24's trap,
  // third time (14-B2 paid for it on the birth form, 14-B3 on the tier ladder). `label` and `meLabel`
  // are optional props, fifteen call sites render this picker, and four of them pass their OWN
  // question. So the catalog is wired to `label = t(language, "subject.label")` INSIDE the component
  // and a caller's own words still win; the twelve call sites that were re-typing the default in
  // English simply stop passing it, which is what makes the default the single answer.
  //
  // The three non-default questions are rows too, because they are the house asking, not a caller
  // being specific: a second person, a partner, a circle-only pick.
  "subject.label": { en: "WHO IS THIS READING FOR?", es: "¿PARA QUIÉN ES ESTA LECTURA?" },
  "subject.labelCircle": { en: "PICK THEM FROM YOUR CIRCLE", es: "ELÍGELOS DE TU CÍRCULO" },
  "subject.labelAlso": { en: "AND WHO ELSE?", es: "¿Y QUIÉN MÁS?" },
  "subject.labelWith": { en: "AND WHO ARE WE READING THEM WITH?", es: "¿Y CON QUIÉN LOS LEEMOS?" },
  "subject.me": { en: "For me", es: "Para mí" },
  // The short one is its own row rather than a truncation of the long one: it sits on a two-chip row
  // inside the bubble modal where the full phrase wraps, and "Yo" is not a prefix of "Para mí".
  "subject.meShort": { en: "Me", es: "Yo" },
  // R85 — **WHAT THE FIRST CONTROL IN EVERY READING ROOM SAYS WHEN IT COULD NOT READ HER OWN CARD.**
  // "For me" is drawn only when a chart for her is in hand, so a failed `/api/profile` read did not
  // draw a broken chip, it drew NO CHIP — and an absent control has no error state (R25). She is left
  // with a picker that offers to read for everybody except her.
  "subject.meFailed": {
    en: "Your own chart could not be read just now. Refresh and “For me” comes back.",
    es: "Tu propia carta no se pudo leer ahora mismo. Actualiza y “Para mí” vuelve.",
  },
  "subject.circle": { en: "My circle", es: "Mi círculo" },
  "subject.new": { en: "+ Someone new", es: "+ Alguien nuevo" },
  // An ACCESSIBLE NAME and a tooltip, both built from a member's own name, which is why the row
  // carries the token rather than being joined at the call site (14-B5's bell-and-mail lesson: a
  // joined string is a sentence Spanish cannot reorder).
  "subject.edit": { en: "Edit {name}", es: "Editar a {name}" },
  // The save offer after a reading cast on someone new. "People of Interest" is the panel's own name
  // and `panel.people.title` already translates it, so the same words are used here rather than a
  // second answer to what that shelf is called.
  "subject.save": { en: "Save {name} to People of Interest", es: "Guarda a {name} en Personas de interés" },
  "subject.saved": { en: "✓ {name} saved to People of Interest", es: "✓ {name} guardada en Personas de interés" },
  // The in-place editor a member reaches from the circle dropdown's pencil.
  "subject.editSave": { en: "Save changes", es: "Guardar los cambios" },
  "subject.editHint": {
    en: "Correct the spelling, birth time, or city. Readings stay attached to this person.",
    es: "Corrige la escritura, la hora de nacimiento o la ciudad. Las lecturas siguen unidas a esta persona.",
  },
  "subject.editNameRequired": { en: "A name is required.", es: "Hace falta un nombre." },
  "subject.editSignIn": { en: "Sign in to edit this person.", es: "Inicia sesión para editar a esta persona." },

  // The Find Your Type reading's four waiting lines — the astrology room's first section, and the
  // one place the picker sits directly above a status line that used to answer in English while the
  // question above it was Spanish. The reading it delivers stays English (`GENERATION_LANGUAGES`).
  "type.title": { en: "Find Your Type", es: "Encuentra tu tipo" },
  "type.go": { en: "Find my type", es: "Encuentra mi tipo" },
  // (Find Your Type's busy label is `reading.goingChart` — the same sentence the mini-report and
  // prediction modals say, so it is one row rather than two that happen to agree today)
  "type.stage1": {
    en: "Casting your chart and reading your Venus, Mars, and Moon...",
    es: "Calculando tu carta y leyendo tu Venus, tu Marte y tu Luna...",
  },
  "type.stage2": {
    en: "Finding the placements that shape who you are drawn to...",
    es: "Buscando las posiciones que forman hacia quién te sientes atraída...",
  },
  "type.stage3": {
    en: "Naming the three types who truly suit you...",
    es: "Nombrando los tres tipos que de verdad te quedan bien...",
  },
  "type.stage4": { en: "Writing your reading...", es: "Escribiendo tu lectura..." },

  // ---- 14-B6c: THE ASTROLOGY ROOM'S OWN SECTIONS ----------------------------------------------
  //
  // **WHAT IS NOT HERE IS THE POINT, AND IT IS 14-B3's SEAM TWICE OVER.** The four button grids
  // (quick readings, predictions, deeper readings, traditions) are FETCHED FROM THE SERVER — their
  // labels, blurbs and tags are composed there, and re-translating them on the client is a second
  // answer key for copy the ledger already recorded. The offering list under "What's in this room?"
  // is `src/catalogs/astrology-menu.ts`, declared on ENGLISH_BY_DESIGN with its reason. What turns
  // is the house ASKING: the headings above those grids, and the one block a member types into.
  "astro.ask.aria": { en: "Ask anything", es: "Pregunta lo que sea" },
  "astro.ask.title": { en: "Want to know more?", es: "¿Quieres saber más?" },
  "astro.ask.lede": {
    en: "Ask anything on your mind. Lala reads your birth chart, today's live transits, and pulls one card, then puts all three together into one straight answer.",
    es: "Pregunta lo que traigas en la cabeza. Lala lee tu carta natal, los tránsitos de hoy y saca una carta, y junta las tres cosas en una sola respuesta directa.",
  },
  "astro.ask.placeholder": {
    en: "Should I take this job? Is this relationship going somewhere? What is this season really about?",
    es: "¿Acepto este trabajo? ¿Esta relación va para algún lado? ¿De qué se trata de verdad esta temporada?",
  },
  "astro.ask.go": { en: "Ask", es: "Pregunta" },
  "astro.ask.going": { en: "Reading your answer...", es: "Leyendo tu respuesta..." },
  // Two accessible names on one control, and they are the pair a screen reader tells apart.
  "astro.ask.dictate": { en: "Dictate your question", es: "Dicta tu pregunta" },
  "astro.ask.dictateStop": { en: "Stop dictation", es: "Deja de dictar" },
  "astro.minis.aria": { en: "Quick astrology readings", es: "Lecturas cortas de astrología" },
  "astro.predictions.aria": { en: "Predictions", es: "Predicciones" },
  "astro.predictions.title": { en: "What's coming?", es: "¿Qué viene?" },
  "astro.deep.aria": { en: "Full astrology readings", es: "Lecturas completas de astrología" },
  "astro.deep.title": { en: "The deeper readings", es: "Las lecturas más profundas" },
  "astro.deep.twoPeople": { en: "Two people", es: "Dos personas" },
  "astro.traditions.aria": { en: "Astrology traditions", es: "Tradiciones de astrología" },
  "astro.traditions.title": { en: "Traditions and timing", es: "Tradiciones y tiempos" },
  "astro.offerings.title": { en: "What's in this room?", es: "¿Qué hay en este cuarto?" },
  "astro.offerings.lede": {
    en: "Everything else your chart can be read for.",
    es: "Todo lo demás que tu carta puede leerte.",
  },

  // ---- 14-B6c: THE FOUR READING MODALS THE ASTROLOGY ROOM RAISES ------------------------------
  //
  // `MiniReportModal`, `DeepReportModal`, `NumerologyMiniModal` and `PredictionModal` are four
  // components with ONE shape, and the copy below is written as SHARED rows for exactly the reason
  // 14-B6b's back door cost a round: six of these sentences were hand-typed in all four of them, so
  // a row read by one modal would have been invisible to a check written per modal. Every `{label}`
  // is the SERVER'S own name for the reading, dropped into a frame that turns — 14-B3's seam said
  // in a placeholder rather than in a comment.
  "reading.failed": {
    en: "Your reading could not be prepared right now. Please try again.",
    es: "Tu lectura no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "reading.signIn": { en: "Sign in to run this reading.", es: "Inicia sesión para hacer esta lectura." },
  "reading.close": { en: "Close reading", es: "Cierra la lectura" },
  // R111 — THE ASTROLOGY ROOM'S CHART FORM. Its own close button hand-typed an accessible name in
  // English while the numerology modal ten screens over already read `reading.close`. It is NOT
  // folded into that row: this control closes a FORM she has typed into, and the two say different
  // things on purpose. The English is byte-for-byte what it was, which is 14-B's standing rule.
  "reading.closeForm": { en: "Close reading form", es: "Cierra el formulario de lectura" },
  // R67: C2's unsaved-close confirm. `language` reached this component and was read on one line.
  "reading.confirmCloseLabel": { en: "Confirm close", es: "Confirma que la cierras" },
  "reading.confirmClose": {
    en: "Close without saving? You can find it in Your Reports.",
    es: "¿La cierras sin guardarla? La encuentras en Tus Informes.",
  },
  "reading.keepOpen": { en: "Keep open", es: "Déjala abierta" },
  "reading.closeAnyway": { en: "Close", es: "Ciérrala" },
  "reading.miniEyebrow": { en: "Mini report", es: "Informe corto" },
  // R111 — and the numerology mini popup's eyebrow says "Mini reading", not "Mini report".
  // `reading.miniEyebrow` was RIGHT THERE and reusing it would have been a copy edit nobody asked
  // for (R56 folded four rows into three; this is the case where folding changes her English).
  "reading.miniReadingEyebrow": { en: "Mini reading", es: "Lectura corta" },
  "reading.modalLabel": { en: "{label} reading", es: "Lectura: {label}" },
  "reading.read": { en: "Read: {label}", es: "Lee: {label}" },
  "reading.going": { en: "Reading...", es: "Leyendo..." },
  "reading.goingChart": { en: "Reading your chart...", es: "Leyendo tu carta..." },
  "reading.goingBoth": { en: "Reading both charts...", es: "Leyendo las dos cartas..." },
  "reading.goingTransits": { en: "Reading your transits...", es: "Leyendo tus tránsitos..." },
  "reading.nameHintSelf": {
    en: "Your birth details, so this is cast on your real chart.",
    es: "Tus datos de nacimiento, para que esto se haga sobre tu carta de verdad.",
  },
  "reading.nameHintOther": {
    en: "Their birth details, so this is cast on their real chart too.",
    es: "Sus datos de nacimiento, para que esto también se haga sobre su carta de verdad.",
  },
  "reading.nameHintHouses": {
    en: "Birth details, so this is cast on the real chart. The exact birth time is required: this reading reads the houses.",
    es: "Datos de nacimiento, para que esto se haga sobre la carta de verdad. La hora exacta es obligatoria: esta lectura lee las casas.",
  },
  // The two birth-time refusals are two rows and not one, because one says YOUR chart and the other
  // says THE chart — the deep readings ask about somebody else as often as about her.
  "reading.needTimeYours": {
    en: "This reading is built from the houses of your chart, and I need your birth time to know them. Check your birth certificate or ask your mother, even close helps, then add your birth time above (uncheck unknown birth time) and I will read this for you.",
    es: "Esta lectura se construye desde las casas de tu carta, y necesito tu hora de nacimiento para saber cuáles son. Busca tu acta de nacimiento o pregúntale a tu mamá, aunque sea aproximada ayuda, y después pon tu hora arriba (desmarca que no sabes la hora) y te la leo.",
  },
  "reading.needTimeThe": {
    en: "This reading is built from the houses of the chart, and I need the exact birth time to know them. Check the birth certificate or ask your mother, even close helps, then add the birth time above (uncheck unknown birth time) and I will read this for you.",
    es: "Esta lectura se construye desde las casas de la carta, y necesito la hora exacta de nacimiento para saber cuáles son. Busca el acta de nacimiento o pregúntale a tu mamá, aunque sea aproximada ayuda, y después pon la hora arriba (desmarca que no sabes la hora) y te la leo.",
  },
  // The waiting lines. Each modal's set is named rather than numbered, so a row a member reads while
  // she waits can be found by the person renaming it (14-B2's counter lesson).
  // R114 — THE BUBBLE READING BELONGS TO NO ROOM, WHICH IS WHY NO ROOM'S TRANSLATION ROUND REACHED
  // IT. `ReadingBubbleModal` is opened from the HOME PAGE showcase, so it is on no room roster and no
  // per-room slice covered it: it was handed a `language`, threaded it to `SubjectPicker`,
  // `CreateRoomForm`, its close button and its save note — and hand-typed every sentence of its own
  // in English. That is 14-B6's own ruling made visible, a lone Spanish frame over an English screen.
  // `bubble.go` is the SUBMIT LABEL, and it is R24/14-B6c's trap a fourth time: `CreateRoomForm`'s own
  // default reads the catalog, and BOTH of this modal's call sites overrode it with English.
  "bubble.eyebrow": { en: "Your reading", es: "Tu lectura" },
  "bubble.modalLabel": { en: "{question} reading", es: "Lectura: {question}" },
  "bubble.casting": {
    en: "Casting your chart and today's transits...",
    es: "Calculando tu carta y los tránsitos de hoy...",
  },
  "bubble.go": { en: "Read it for me", es: "Léemelo" },
  // SP2's second-person selector. `subject.labelWith` says almost this and is NOT reused: that row
  // asks who to read them WITH, and this one asks who the other person IS (R111's ruling — folding
  // two rows that nearly agree is a copy edit nobody asked for).
  "bubble.secondLabel": { en: "WHO IS THE OTHER PERSON?", es: "¿QUIÉN ES LA OTRA PERSONA?" },
  "bubble.secondCircle": { en: "Someone in my circle", es: "Alguien de mi círculo" },
  "bubble.secondNew": { en: "Someone new", es: "Alguien nuevo" },
  "bubble.secondUnnamed": { en: "I'd rather not name them", es: "Prefiero no decir su nombre" },
  "bubble.secondNote": {
    en: "Good. I will read this from your chart, your transits right now, and the cards.",
    es: "Bien. Esto lo leo desde tu carta, tus tránsitos de ahora mismo y las cartas.",
  },
  "bubble.chooseSecond": {
    en: "Choose who the other person is first, so I can read the two of you together.",
    es: "Primero elige quién es la otra persona, para que pueda leerlos a los dos juntos.",
  },
  // R114 — the two remaining English literals in `PredictionModal`. Its eyebrow is NOT a room name
  // (14-B5's brand ruling): the modal one screen over already reads `reading.miniEyebrow`, so the
  // eyebrow on this shape turns. The sign picker's label is the only field label in that arm.
  "prediction.eyebrow": { en: "Prediction", es: "Predicción" },
  "prediction.whichSign": { en: "WHICH SIGN?", es: "¿QUÉ SIGNO?" },
  "cast.chart": { en: "Casting your chart...", es: "Calculando tu carta..." },
  "cast.bothCharts": { en: "Casting both charts...", es: "Calculando las dos cartas..." },
  "deep.overlay": { en: "Laying them over each other...", es: "Poniendo una encima de la otra..." },
  "deep.between": { en: "Reading what is really between you...", es: "Leyendo lo que de verdad hay entre ustedes..." },
  "deep.carries": { en: "Reading what it carries...", es: "Leyendo lo que carga..." },
  "num.bothNumbers": { en: "Calculating both sets of numbers...", es: "Calculando los números de las dos..." },
  "num.harmonise": {
    en: "Reading where you harmonise and where you grate...",
    es: "Leyendo dónde armonizan y dónde se raspan...",
  },
  "num.writingCompat": { en: "Writing your compatibility...", es: "Escribiendo su compatibilidad..." },
  "num.calculating": { en: "Calculating your {label}...", es: "Calculando tu {label}..." },
  "num.meansForYou": { en: "Reading what it means for you...", es: "Leyendo lo que significa para ti..." },
  // R211: the numerology room's own rotating stages. Six of the eleven sentences in
  // `src/lib/numerology-stages.ts` were BYTE-IDENTICAL second copies of rows already sitting in
  // this catalog in both languages (`num.bothNumbers`, `num.harmonise`, `num.writingCompat`,
  // `num.meansForYou` among them) — so the Spanish was written, shipped and read by other surfaces
  // while this room showed the English copy (R28/R29, on her screen). These five had no row at all.
  // LF-1 caught this row's first Spanish in the round that wrote it: Spanish puts the adjective
  // AFTER the noun, so the direct translation of "your core numbers" is `tus números centrales` and
  // the banned bigram lands adjacent where the English never does. `los números` is legal and is the
  // catalog's own existing phrasing ("leer los números escondidos en tu nombre completo").
  "num.coreNumbers": { en: "Calculating your core numbers...", es: "Calculando los números centrales de tu nombre..." },
  "num.lifePathExpression": { en: "Reading your Life Path and Expression...", es: "Leyendo tu Camino de Vida y tu Expresión..." },
  "num.soulPersonality": { en: "Reading your Soul Urge and Personality...", es: "Leyendo tu Impulso del Alma y tu Personalidad..." },
  "num.writingFull": { en: "Writing your full reading...", es: "Escribiendo tu lectura completa..." },
  "num.comparingPaths": { en: "Comparing your Life Paths...", es: "Comparando sus Caminos de Vida..." },
  "num.calculatingNumber": { en: "Calculating your number...", es: "Calculando tu número..." },
  "num.writingReading": { en: "Writing your reading...", es: "Escribiendo tu lectura..." },
  "num.nameHintOther": {
    en: "Their full name at birth, including their middle name, so their numbers are accurate.",
    es: "Su nombre completo al nacer, con su segundo nombre, para que sus números sean exactos.",
  },
  "pred.signTransits": { en: "Reading the transits for your sign...", es: "Leyendo los tránsitos de tu signo..." },
  "pred.moving": { en: "Reading what is moving against it...", es: "Leyendo lo que se mueve encima de ella..." },
  "ask.stage2": { en: "Reading today's live transits...", es: "Leyendo los tránsitos de hoy..." },
  "ask.stage3": { en: "Drawing your card...", es: "Sacando tu carta..." },
  "ask.stage4": { en: "Reading the three together...", es: "Leyendo las tres cosas juntas..." },
  "mini.stage2": { en: "Reading the placements that matter...", es: "Leyendo las posiciones que importan..." },
  "acg.stage2": { en: "Drawing your lines across the earth...", es: "Trazando tus líneas por la tierra..." },
  "acg.stage3": { en: "Reading your map...", es: "Leyendo tu mapa..." },

  // The astrology READING modal — the tile route every offering in the room's own list opens into.
  // Its delivery toolbar is one of the two surfaces 14-B6a left with its own LAYOUT; it keeps the
  // layout and takes its SENTENCES from `result.*` and `reports.download`, which is what that
  // ruling actually said. `modal.onScreen` is the toolbar's own word and is the only new one.
  "modal.saveFailed": { en: "This report could not be saved yet.", es: "Este informe todavía no se pudo guardar." },
  "modal.chartFailed": { en: "This chart could not be prepared yet.", es: "Esta carta todavía no se pudo preparar." },
  "modal.generateReport": { en: "Generate full report", es: "Haz el informe completo" },
  // R112: `modal.preparing` and `modal.submit` were the raw-text arm's button, and `modal.question*`
  // / `modal.placeholder*` Tarot pair was the `mode === "tarot"` fork nothing ever passed. All four
  // are DELETED with the arm — a row nobody reads is a translation no member will ever see, which is
  // this catalog's own law and is what caught them within the hour.
  "modal.castingEvery": {
    en: "Casting your chart and interpreting every placement…",
    es: "Calculando tu carta e interpretando cada posición…",
  },
  // CHART-WAIT-Q1 (her YES, 2026-10-12): the progress note on a long chart, and the note on one whose words are still being written.
  "modal.chartStillWriting": {
    en: "Your chart is cast and its readings are being written. This can take up to two minutes, so keep this open.",
    es: "Tu carta está calculada y sus lecturas se están escribiendo. Puede tardar hasta dos minutos, así que mantén esto abierto.",
  },
  "modal.chartWordsPending": {
    en: "Some of your chart's readings are still being written. They will fill in on their own, and we'll let you know when your full chart is ready in your room.",
    es: "Algunas lecturas de tu carta todavía se están escribiendo. Se completarán solas y te avisaremos cuando tu carta completa esté lista en tu habitación.",
  },
  // ENTITLEMENT-UNREADABLE-Q1 (her words, 2026-10-12): said instead of the sign-up page when her membership cannot be read.
  "membership.unread": {
    en: "We can't reach your membership right now. Please try again in a moment.",
    es: "No podemos acceder a tu membresía en este momento. Inténtalo de nuevo en un momento.",
  },
  "modal.onScreen": { en: "On-screen report", es: "Informe en pantalla" },
  "modal.questionFocus": { en: "Question or focus", es: "Pregunta o enfoque" },
  "modal.placeholderFocus": {
    en: "Love, timing, work, compatibility, spiritual clarity...",
    es: "Amor, tiempos, trabajo, compatibilidad, claridad espiritual...",
  },

  // ---- 14-B6c: THE ASTROCARTOGRAPHY ROOM -------------------------------------------------------
  //
  // The room's eyebrow is the roster's own name and stays English (14-B5's biconditional). The line
  // REFERENCE below it — planet names, line types, angles, keywords, meanings — is the VENDOR'S
  // data fetched at runtime and is 14-B3's seam untouched: a translated frame around "Venus MC"
  // reads better than a half-translated table, and this house does not paraphrase vendor readings.
  "acg.title": { en: "Where Your Chart Works For You", es: "Dónde tu carta trabaja para ti" },
  "acg.lede": {
    en: "Your chart is about WHERE as much as when. Stretched across the earth, every planet draws a line, and where a line falls it turns that part of life up loud.",
    es: "Tu carta habla de DÓNDE tanto como de cuándo. Estirada sobre la tierra, cada planeta traza una línea, y donde cae una línea sube el volumen de esa parte de tu vida.",
  },
  "acg.go": { en: "Draw my map", es: "Traza mi mapa" },
  "acg.going": { en: "Drawing your map...", es: "Trazando tu mapa..." },
  "acg.nameHint": {
    en: "Your birth details, so your lines are cast on your real chart. Your exact birth time is required here: the lines ARE the angles of your chart.",
    es: "Tus datos de nacimiento, para que tus líneas se tracen sobre tu carta de verdad. Aquí tu hora exacta es obligatoria: las líneas SON los ángulos de tu carta.",
  },
  "acg.needTime": {
    en: "Astrocartography is built from the angles of your chart, and those come from your exact birth time. Without it there are no lines to draw. Check your birth certificate or ask your mother, even close helps, then add your birth time above (uncheck unknown birth time) and I will map you.",
    es: "La astrocartografía se construye desde los ángulos de tu carta, y esos salen de tu hora exacta de nacimiento. Sin ella no hay líneas que trazar. Busca tu acta de nacimiento o pregúntale a tu mamá, aunque sea aproximada ayuda, y después pon tu hora arriba (desmarca que no sabes la hora) y te hago el mapa.",
  },
  "acg.tools.aria": { en: "Analyze a place", es: "Analiza un lugar" },
  "acg.tools.title": { en: "Read a place", es: "Lee un lugar" },
  "acg.tools.lede": {
    en: "Name anywhere on earth and I will tell you which of your lines reach it, and what a move there would really do.",
    es: "Di cualquier lugar de la tierra y te digo cuáles de tus líneas llegan ahí, y lo que una mudanza allá te haría de verdad.",
  },
  // The example in the search box is a REAL PLACE, so it stays exactly as it is in both languages —
  // a translated city name typed into a geocoder is a lookup that finds nothing.
  "acg.tools.placeholder": { en: "Los Angeles, California, United States", es: "Los Angeles, California, United States" },
  "acg.tools.pickFirst": {
    en: "Pick a place from the list first, so I have its exact coordinates.",
    es: "Primero elige un lugar de la lista, para que tenga sus coordenadas exactas.",
  },
  "acg.location.go": { en: "What does this place do for me?", es: "¿Qué hace este lugar por mí?" },
  "acg.location.going": { en: "Reading the place...", es: "Leyendo el lugar..." },
  "acg.relocation.go": { en: "What if I moved here?", es: "¿Y si me mudo aquí?" },
  "acg.relocation.going": { en: "Reading the move...", es: "Leyendo la mudanza..." },
  // HOUSE READINGS (e) — CHECK A PLACE, her new reading, shown only when the server's switch is on.
  "acg.check.title": { en: "Check a Place", es: "Revisa un lugar" },
  "acg.check.lede": {
    en: "Name a city, a state or a country, and I will tell you how that place works for the person you pick: what it opens for them, and where it runs harder.",
    es: "Di una ciudad, un estado o un país, y te digo cómo funciona ese lugar para la persona que elijas: lo que le abre, y dónde se pone más difícil.",
  },
  // A REAL PLACE in both languages, for the same reason as the box above.
  "acg.check.placeholder": { en: "Texas, United States", es: "Texas, United States" },
  "acg.check.go": { en: "Check this place", es: "Revisa este lugar" },
  "acg.check.going": { en: "Checking the place...", es: "Revisando el lugar..." },
  "acg.signIn": { en: "Sign in to map your chart.", es: "Inicia sesión para trazar el mapa de tu carta." },
  "acg.failed": {
    en: "Your map could not be prepared right now. Please try again.",
    es: "Tu mapa no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  // ONE row, read twice: the section's heading IS its accessible name here, and two rows that agree
  // today are two answers to one question (this catalog's own disease, `hd.stage1`'s precedent).
  "acg.lines.title": { en: "What the lines mean", es: "Qué significan las líneas" },
  "acg.lines.lede": {
    en: "Four lines per planet: rising, setting, overhead, and underfoot.",
    es: "Cuatro líneas por planeta: saliendo, poniéndose, encima y debajo de ti.",
  },
  "acg.stage.near": { en: "Finding the lines near {place}...", es: "Buscando las líneas cerca de {place}..." },
  "acg.stage.close": { en: "Measuring how close they run...", es: "Midiendo qué tan cerca pasan..." },
  "acg.stage.does": { en: "Reading what this place does to you...", es: "Leyendo lo que este lugar te hace..." },
  "acg.stage.recast": { en: "Recasting your chart at {place}...", es: "Recalculando tu carta en {place}..." },
  "acg.stage.moves": { en: "Seeing what moves...", es: "Viendo qué se mueve..." },

  // ---- R103: THE PLACE PICKER, WHICH IS THE FIRST CONTROL IN THIS ROOM AND WAS THE LAST ENGLISH
  // IN IT. 14-B6c turned astrocartography around it; `PlaceSearch` took no `language` prop, so six
  // sentences stayed hand-typed inside a Spanish room. **They are ONE round rather than one row,
  // which is 14-B6c's own ruling: a lone translated line over an untranslated screen is the defect.**
  // The placeholder above is the exception and it is already declared on `SAME_IN_BOTH` — a
  // translated city name typed into a geocoder is a lookup that finds nothing.
  "acg.place.which": { en: "Which place?", es: "¿Qué lugar?" },
  // The place's own label is the geocoder's, so it rides in as a TOKEN and is never reformatted
  // (R24's shape): "Stonehenge, Amesbury, Wiltshire, ENG, United Kingdom" is that place's name.
  "acg.place.picked": { en: "Reading for {place}", es: "Lectura para {place}" },
  // AC2/AC3: the honest no-match, and it says only this. The "try adding the country" coaching was
  // removed on owner instruction and is not reintroduced by a translation.
  "acg.place.none": { en: "No match found.", es: "No se encontró ningún lugar." },
  // R97's state, and it is a DIFFERENT sentence from the one above it: the house's geocoder did not
  // answer, which is not a claim about her town existing.
  "acg.place.unavailable": {
    en: "The place lookup did not answer just then. Try that once more.",
    es: "La búsqueda de lugares no respondió en ese momento. Inténtalo una vez más.",
  },
  "acg.place.searching": { en: "Looking...", es: "Buscando..." },
  "acg.place.hint": {
    en: "Start typing any place on earth: a town, a village, a neighbourhood, an island, a landmark, even a street address. Then pick it from the list.",
    es: "Empieza a escribir cualquier lugar de la tierra: un pueblo, una aldea, un barrio, una isla, un punto de referencia, incluso una dirección. Después elígelo de la lista.",
  },

  // ---- 14-B6c: THE TAROT ROOM ------------------------------------------------------------------
  //
  // The room's own words turn; the SPREADS do not. `src/catalogs/tarot-spreads.ts` is held to the
  // SERVER'S own keys (R6-3), and a category's label rides into `spreadLabel` — which becomes the
  // saved reading's TITLE on her shelf and the kicker on its PDF. It is declared on
  // ENGLISH_BY_DESIGN beside the astrology offerings, for the same reason and with the same flag.
  "tarot.title": { en: "Tarot Reading Room", es: "Cuarto de lectura de tarot" },
  "tarot.mantra": { en: "Relax. Breathe. Ask. Pull.", es: "Relájate. Respira. Pregunta. Saca." },
  "tarot.one.eyebrow": { en: "Section one", es: "Primera parte" },
  "tarot.one.title": { en: "The cards pull for you", es: "Las cartas se sacan solas para ti" },
  "tarot.one.blurb": {
    en: "Ask your question, choose how you want the cards laid, and the site pulls them for you.",
    es: "Haz tu pregunta, elige cómo quieres que se tiendan las cartas, y el sitio las saca por ti.",
  },
  "tarot.ask": { en: "Ask your question", es: "Haz tu pregunta" },
  "tarot.askPlaceholder": {
    en: "Ask the cards about love, work, timing, a decision, or a season of life…",
    es: "Pregúntale a las cartas por el amor, el trabajo, los tiempos, una decisión o una temporada de tu vida…",
  },
  // Found by CAPTURE 17, not by the 203 source checks: a step line between the question box and the
  // spread grid, and the only English left on an otherwise Spanish room. The grid under it is the
  // declared exception, so the two sit one line apart and the seam is deliberate on one side only.
  "tarot.chooseSpread": { en: "Now choose your spread", es: "Ahora elige tu tirada" },
  "tarot.pull": { en: "Pull the cards", es: "Saca las cartas" },
  "tarot.pulling": { en: "Pulling your cards…", es: "Sacando tus cartas…" },
  "tarot.pullingOne": { en: "Pulling your card…", es: "Sacando tu carta…" },
  "tarot.facedown": {
    en: "Your cards are drawn. Take a breath, hold your question, and turn them over.",
    es: "Tus cartas están sacadas. Respira, sostén tu pregunta y voltéalas.",
  },
  // The toolbar's own word on the surface 14-B6a left with its own LAYOUT.
  "tarot.yourReading": { en: "Your reading", es: "Tu lectura" },
  "tarot.saveFailed": { en: "This reading could not be saved yet.", es: "Esta lectura todavía no se pudo guardar." },
  "tarot.saveSignIn": {
    en: "Sign in to save this reading to your room.",
    es: "Inicia sesión para guardar esta lectura en tu cuarto.",
  },
  "tarot.dailyNotReady": { en: "The daily card is not ready yet.", es: "La carta del día todavía no está lista." },
  "tarot.drawFailed": {
    en: "The cards could not be drawn right now.",
    es: "Las cartas no se pudieron sacar en este momento.",
  },
  "tarot.library": { en: "Card Library", es: "Biblioteca de cartas" },
  "tarot.libraryBlurb": {
    en: "Look up and learn any of the 78 cards",
    es: "Busca y aprende cualquiera de las 78 cartas",
  },
  // Section two: her OWN physical spread, photographed and read.
  "tarot.photo.choose": { en: "Choose a photo", es: "Elige una foto" },
  "tarot.photo.chooseAgain": { en: "Choose a different photo", es: "Elige otra foto" },
  "tarot.photo.library": { en: "Choose from library", es: "Elige de la galería" },
  "tarot.photo.notImage": {
    en: "Please choose a photo of your spread (a JPEG or PNG).",
    es: "Elige una foto de tu tirada (un JPEG o un PNG).",
  },
  "tarot.photo.preparing": { en: "Getting your photo ready...", es: "Preparando tu foto..." },
  "tarot.photo.failed": {
    en: "That photo did not come through. Please choose it again.",
    es: "Esa foto no llegó bien. Elígela otra vez.",
  },
  "tarot.photo.needPhoto": { en: "Add a photo of your spread first.", es: "Primero agrega una foto de tu tirada." },
  "tarot.analyze.laid": { en: "What did you lay?", es: "¿Qué tendiste?" },
  "tarot.analyze.about": { en: "What is this reading about?", es: "¿De qué se trata esta lectura?" },
  "tarot.analyze.aboutPlaceholder": {
    en: "Tell her what you were asking when you pulled these cards…",
    es: "Dile qué estabas preguntando cuando sacaste estas cartas…",
  },
  "tarot.analyze.unreadable": { en: "Tell me the cards I could not read", es: "Dime las cartas que no pude leer" },
  // **THE CARD NAMES DO NOT TURN AND THE WORD AROUND THEM DOES.** A member types what she is looking
  // at into this box and the server matches it against the deck, whose names are English on this
  // site (TarotCardImage keys on the slug). "La Torre" is a card this house cannot find.
  "tarot.analyze.unreadablePlaceholder": {
    en: "The Tower, Two of Cups reversed…",
    es: "The Tower, Two of Cups al revés…",
  },
  "tarot.analyze.go": { en: "Read my spread", es: "Lee mi tirada" },
  "tarot.analyze.goWithCards": { en: "Read it with these cards", es: "Léela con estas cartas" },
  "tarot.analyze.going": { en: "Reading your spread...", es: "Leyendo tu tirada..." },
  "tarot.analyze.signIn": {
    en: "Sign in to have your spread read.",
    es: "Inicia sesión para que te lean tu tirada.",
  },
  "tarot.analyze.failed": {
    en: "Your reading could not be prepared right now. Please try again in a moment.",
    es: "Tu lectura no se pudo preparar en este momento. Inténtalo de nuevo en un momento.",
  },
  "tarot.stage1": { en: "Looking at your spread...", es: "Mirando tu tirada..." },
  "tarot.stage2": { en: "Naming your cards...", es: "Nombrando tus cartas..." },
  "tarot.stage3": { en: "Reading them against your question...", es: "Leyéndolas contra tu pregunta..." },

  // ---- 14-B7a: THE PETITION GENERATOR, WHICH IS THE ONE SCREEN THAT PROVES ITEM 14(a) ----------
  //
  // **THE LABELS AROUND THE PICKER TURN AND THE EIGHT NAMES INSIDE IT DO NOT.** `divine-name` is on
  // `SACRED_PARTS`, and `PETITION_DIVINE_NAMES` (`src/magicRoom.js`) is EIGHT DIVINE NAMES RENDERED
  // AS OPTIONS — El Shaddai, Yahweh, Jesus, Jehovah, Elohim, Elohim Gibor, Elohim Tzaabaoth, Allah.
  // So there is NO row here for any of them, and the gate holds that as a biconditional over the
  // roster BY IMPORT rather than as this comment. **This is 14(a)'s law standing on a real surface**,
  // which is smaller and truer than the claim the pointer inherited: every OTHER sacred part on that
  // roster is generated content, and generated content is English until 14(d).
  //
  // The room's eyebrow ("Magic Room · Petition") is `brand.` by 14-B5's biconditional and stays.
  "petition.title": { en: "Write your petition paper", es: "Escribe tu papel de petición" },
  "petition.lede": {
    en: "Tell me the situation, in your own words. I write you a true petition paper, your desire set down as already lived, never asked for, in the name you choose. This is the way of the spoken word.",
    es: "Cuéntame la situación, con tus propias palabras. Yo te escribo un papel de petición de verdad, tu deseo puesto como ya vivido, nunca pedido, en el nombre que tú elijas. Así es el camino de la palabra hablada.",
  },
  "petition.placeholder": {
    en: "My rent is paid in full and on time, my work is steady, and my home is peaceful...",
    es: "Mi renta está pagada completa y a tiempo, mi trabajo es estable, y mi casa está en paz...",
  },
  "petition.inTheNameOf": { en: "Speak it in the name of", es: "Dilo en el nombre de" },
  "petition.target.hint": {
    en: "This petition is worked on someone. Their name is written on the paper and crossed with your command.",
    es: "Esta petición se trabaja sobre alguien. Su nombre se escribe en el papel y se cruza con tu mandato.",
  },
  "petition.target.name": { en: "Their name", es: "Su nombre" },
  "petition.target.nameLabel": { en: "Target name", es: "Nombre de la persona" },
  "petition.target.birth": { en: "Their birthday (optional)", es: "Su cumpleaños (opcional)" },
  "petition.target.birthLabel": { en: "Target birthday", es: "Cumpleaños de la persona" },
  "petition.target.off": { en: "This is for me, not another person", es: "Esto es para mí, no para otra persona" },
  "petition.target.on": {
    en: "Is this petition worked on someone? Add their name",
    es: "¿Esta petición se trabaja sobre alguien? Pon su nombre",
  },
  "petition.go": { en: "Get My Petition", es: "Dame mi petición" },
  "petition.going": { en: "Writing your petition...", es: "Escribiendo tu petición..." },
  // R211: this generator was the ONE of twenty-one whose rotating stages were raw English strings,
  // and its "tell me first" line was the one of six Magic Room generators not resolving through the
  // catalog. Its first stage reuses `working.sitting`, which the room already owns; these three are
  // its own. **The wording is her petition METHOD's framing and not the petition FORMULA** — ITEM
  // 14a's SACRED_PARTS covers the words of the petition itself, which nothing here composes.
  "petition.needSituation": {
    en: "Tell me the situation first, in your own words, so your petition speaks to it.",
    es: "Cuéntame primero la situación, con tus propias palabras, para que tu petición le hable.",
  },
  "petition.stage.choosing": {
    en: "Choosing the words that already carry it...",
    es: "Eligiendo las palabras que ya lo cargan...",
  },
  "petition.stage.speaking": { en: "Speaking it as already lived...", es: "Diciéndolo como ya vivido..." },
  "petition.stage.writing": { en: "Writing your petition paper...", es: "Escribiendo tu papel de petición..." },
  "petition.signIn": {
    en: "Sign in to have your petition written.",
    es: "Inicia sesión para que te escriban tu petición.",
  },
  "petition.failed": {
    en: "Your petition could not be prepared right now. Please try again.",
    es: "Tu petición no se pudo preparar en este momento. Inténtalo de nuevo.",
  },

  // ---- 14-B7b: THE REST OF THE MAGIC ROOM'S GENERATORS -----------------------------------------
  //
  // **THE LINE THIS SLICE DRAWS IS BETWEEN A NAME AND A SENTENCE, AND IT IS 14-B5's BICONDITIONAL
  // ONE ROOM DOWN.** `src/catalogs/magic-books.ts` is the roster that NAMES this room's books, so
  // the four headings that repeat a book's name (The Sixth and Seventh Books of Moses, The Greater
  // Keys of Solomon, The Book of Roots, The Conjure Spellbook, The Cauldron) keep the house's own
  // word for them, exactly as the room eyebrows do; every SENTENCE around them turns. The two
  // headings that are not on that roster are imperatives rather than names, and they turn.
  //
  // **AND THE DELIVERED WORKING IS NOT IN HERE AT ALL.** Every stage line, refusal and label below
  // is the room speaking; the working itself is generated content, English until 14(d), and the
  // notepaper's saved document is her own words. Neither has a row.
  //
  // `working.` is the family the four situation-to-working generators SHARE. Four rooms saying one
  // sentence four times is how a sentence drifts inside a catalog (14-B6's back door), so the
  // sentence is one row and each room reads it.
  "working.needSituation": {
    en: "Tell me your situation first, in your own words.",
    es: "Cuéntame primero tu situación, con tus propias palabras.",
  },
  "working.sitting": { en: "Sitting with your situation...", es: "Sintiendo tu situación..." },
  "working.writing": { en: "Writing your working...", es: "Escribiendo tu obra..." },
  "working.writingOut": { en: "Writing it out...", es: "Escribiéndolo..." },
  "working.failed": {
    en: "Your working could not be prepared right now. Please try again.",
    es: "Tu obra no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "working.downloadSeal": { en: "Download the seal", es: "Descarga el sello" },

  // ---- Spell Your Oils ----
  "oils.title": { en: "Spell Your Oils", es: "Conjura tus aceites" },
  "oils.needPurpose": {
    en: "Tell me what the oil is for, in your own words.",
    es: "Dime para qué es el aceite, con tus propias palabras.",
  },
  "oils.placeholder": {
    en: "An oil to draw money and steady work to me... / to protect my home... / to call a love back to me...",
    es: "Un aceite para atraerme dinero y trabajo estable... / para proteger mi casa... / para traerme de vuelta un amor...",
  },
  "oils.traditionsAria": { en: "Choose your tradition", es: "Elige tu tradición" },
  "oils.target.hint": {
    en: "This oil is worked toward a specific person. Their name and birthday go on the candle you dress.",
    es: "Este aceite se trabaja hacia una persona en particular. Su nombre y su cumpleaños van en la vela que vistes.",
  },
  "oils.target.off": { en: "No specific person", es: "Ninguna persona en particular" },
  "oils.target.on": {
    en: "Is this oil for a specific person? Add their name",
    es: "¿Este aceite es para una persona en particular? Pon su nombre",
  },
  "oils.name.label": { en: "Name your oil", es: "Nombra tu aceite" },
  "oils.name.placeholder": {
    en: "Money Draw Oil, Road Opener, whatever you call it",
    es: "Aceite de atraer dinero, Abre Caminos, como tú lo llames",
  },
  // "Conjure Oil" is the SHELF's own label for an unnamed oil (`CREATION_TYPES`), so it is quoted as
  // the fact it is rather than translated into a name her shelf will never show her.
  "oils.name.hint": {
    en: "Optional. Leave it blank and it goes on your shelf as Conjure Oil. You can rename it there any time.",
    es: "Opcional. Si lo dejas en blanco va a tu estante como Conjure Oil. Puedes cambiarle el nombre ahí cuando quieras.",
  },
  "oils.go": { en: "Spell my oil", es: "Conjura mi aceite" },
  "oils.signIn": { en: "Sign in to spell your oil.", es: "Inicia sesión para conjurar tu aceite." },
  "oils.failed": {
    en: "Your oil could not be spelled right now. Please try again.",
    es: "Tu aceite no se pudo conjurar en este momento. Inténtalo de nuevo.",
  },
  "oils.stage.sitting": { en: "Sitting with what you need...", es: "Sintiendo lo que necesitas..." },
  "oils.stage.carrier": { en: "Choosing your carrier and herbs...", es: "Eligiendo tu aceite base y tus hierbas..." },
  "oils.stage.spelling": { en: "Spelling your oil...", es: "Conjurando tu aceite..." },
  "oils.stage.seal": { en: "Drawing the seal for your working...", es: "Trazando el sello para tu obra..." },
  "oils.offer.lede": { en: "Lala already has an oil for this.", es: "Lala ya tiene un aceite para esto." },
  "oils.offer.spelledWith": { en: "Spelled with:", es: "Conjurado con:" },
  "oils.offer.herbs": { en: "Herbs in the candle:", es: "Hierbas en la vela:" },
  "oils.offer.work": { en: "Work {title}", es: "Trabaja {title}" },
  "oils.offer.compose": {
    en: "None of these. Compose one for my situation",
    es: "Ninguno de estos. Compón uno para mi situación",
  },

  // ---- The Sixth and Seventh Books of Moses ----
  "moses.lede": {
    en: "Tell me your situation. I draw the working from the Sixth and Seventh Books of Moses that serves it, and teach you how to work it.",
    es: "Cuéntame tu situación. Saco de los Libros Sexto y Séptimo de Moisés la obra que te sirve, y te enseño a trabajarla.",
  },
  "moses.placeholder": {
    en: "I need protection over my home, and to break what has been working against me...",
    es: "Necesito protección sobre mi casa, y romper lo que ha estado obrando en mi contra...",
  },
  "moses.go": { en: "Get My Seal", es: "Dame mi sello" },
  "moses.going": { en: "Opening the Books...", es: "Abriendo los Libros..." },
  "moses.signIn": {
    en: "Sign in to work the Books of Moses.",
    es: "Inicia sesión para trabajar los Libros de Moisés.",
  },
  "moses.stage.opening": { en: "Opening the Books of Moses...", es: "Abriendo los Libros de Moisés..." },
  "moses.stage.finding": { en: "Finding the working that serves you...", es: "Buscando la obra que te sirve..." },

  // ---- The Greater Keys of Solomon ----
  "keys.lede": {
    en: "Tell me your situation. I draw the pentacle of the Greater Keys of Solomon that serves it, show you its seal, and teach you how to work it the Conjured Hoodoo way.",
    es: "Cuéntame tu situación. Saco el pentáculo de las Claves Mayores de Salomón que te sirve, te muestro su sello, y te enseño a trabajarlo al modo del Conjured Hoodoo.",
  },
  "keys.placeholder": {
    en: "I need protection over my home and my family, and to turn back what has been sent against me...",
    es: "Necesito protección sobre mi casa y mi familia, y devolver lo que han mandado en mi contra...",
  },
  "keys.go": { en: "Get My Pentacle", es: "Dame mi pentáculo" },
  "keys.going": { en: "Turning the Keys...", es: "Girando las Claves..." },
  "keys.signIn": { en: "Sign in to work the Keys.", es: "Inicia sesión para trabajar las Claves." },
  "keys.stage.turning": {
    en: "Turning the Greater Keys of Solomon...",
    es: "Girando las Claves Mayores de Salomón...",
  },
  "keys.stage.drawing": { en: "Drawing the right pentacle...", es: "Trazando el pentáculo que sirve..." },

  // ---- The Book of Roots ----
  "herbs.lede": {
    en: "Tell me what you need herbs for: healing, money, a court case, love, protection, anything. I will give you the herbs and roots that serve it, each with its meaning, leaning on what you can keep in the house. Then send them to the Cauldron to work them into a spell.",
    es: "Dime para qué necesitas hierbas: sanación, dinero, un caso en la corte, amor, protección, lo que sea. Te doy las hierbas y raíces que sirven, cada una con su significado, apoyándome en lo que puedes tener en casa. Luego mándalas al Caldero para convertirlas en un hechizo.",
  },
  "herbs.needTopic": {
    en: "Tell me what you need herbs for, in your own words.",
    es: "Dime para qué necesitas hierbas, con tus propias palabras.",
  },
  "herbs.placeholder": { en: "what do you need herbs for?", es: "¿para qué necesitas hierbas?" },
  "herbs.go": { en: "Get My Herbs", es: "Dame mis hierbas" },
  "herbs.going": { en: "Gathering your herbs...", es: "Recogiendo tus hierbas..." },
  "herbs.signIn": { en: "Sign in to get your herbs.", es: "Inicia sesión para recibir tus hierbas." },
  "herbs.failed": {
    en: "That could not be prepared right now. Please try again.",
    es: "Eso no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "herbs.stage.reading": { en: "Reading your aim...", es: "Leyendo tu propósito..." },
  "herbs.stage.turning": { en: "Turning to the herbs...", es: "Yendo a las hierbas..." },
  "herbs.stage.gathering": { en: "Gathering the roots for you...", es: "Reuniendo las raíces para ti..." },

  // ---- Your Notepaper (not a book on the shelf, so its heading turns) ----
  "note.title": { en: "Your Notepaper", es: "Tu papel de notas" },
  "note.lede": {
    en: "Write or paste your own words here: your own petition, an incantation, a chant, anything. They ride into a working exactly as you write them, and you can keep them on your shelf.",
    es: "Escribe o pega aquí tus propias palabras: tu propia petición, un encantamiento, un canto, lo que sea. Entran en la obra exactamente como las escribes, y puedes guardarlas en tu estante.",
  },
  "note.titlePlaceholder": { en: "Give it a title (optional)", es: "Ponle un título (opcional)" },
  "note.bodyPlaceholder": { en: "Write your own words here...", es: "Escribe aquí tus propias palabras..." },
  "note.needText": { en: "Write something first.", es: "Escribe algo primero." },
  "note.signIn": { en: "Sign in to save this.", es: "Inicia sesión para guardar esto." },
  "note.save": { en: "Save to Shelf", es: "Guardar en el estante" },
  "note.saved": { en: "✓ Saved to shelf", es: "✓ Guardado en el estante" },
  "note.savedNote": {
    en: "Saved to your Magic Room shelf.",
    es: "Guardado en el estante de tu Magic Room.",
  },

  // ---- The Cauldron, and the ONE sender eleven surfaces reach it through ----
  "cauldron.lede": {
    en: "Send your workings here, your petition, your Greater Keys seal, your spells, oils, and herbs, and the Cauldron draws them into one complete working with a sensible order to perform it.",
    es: "Manda aquí tus obras, tu petición, tu sello de las Claves Mayores, tus hechizos, aceites y hierbas, y el Caldero las junta en una sola obra completa, con un orden sensato para realizarla.",
  },
  "cauldron.looking": { en: "Looking into the Cauldron...", es: "Mirando dentro del Caldero..." },
  "cauldron.empty": {
    en: "The Cauldron is empty. From any working in this room, choose Send to Cauldron, then come back to bring them together.",
    es: "El Caldero está vacío. Desde cualquier obra de este cuarto, elige Mandar al Caldero, y vuelve para juntarlas.",
  },
  "cauldron.needItems": {
    en: "Send some workings to the Cauldron first.",
    es: "Manda primero algunas obras al Caldero.",
  },
  "cauldron.remove": { en: "Remove from the Cauldron", es: "Quitar del Caldero" },
  // R64 — WHAT THE POT SAYS WHEN THE ANSWER DID NOT LAND. Deliberately NOT `cauldron.empty`: that
  // one tells her to go and send workings here, which over an unread pot reads as hers being gone.
  "cauldron.unread": {
    en: "The Cauldron could not be read just now. Nothing in it was lost. Try again in a moment.",
    es: "El Caldero no se pudo leer ahora mismo. No se perdió nada de lo que hay dentro. Inténtalo de nuevo en un momento.",
  },
  "cauldron.potFailed": {
    en: "The Cauldron did not answer just now. Nothing in it was lost.",
    es: "El Caldero no respondió ahora mismo. No se perdió nada de lo que hay dentro.",
  },
  "cauldron.name.label": { en: "Name this working", es: "Nombra esta obra" },
  "cauldron.name.placeholder": { en: "What do you call this working?", es: "¿Cómo llamas a esta obra?" },
  "cauldron.name.hint": {
    en: "Optional. Blank goes on your shelf as Cauldron Working, and you can rename it there.",
    es: "Opcional. En blanco va a tu estante como Cauldron Working, y puedes cambiarle el nombre ahí.",
  },
  "cauldron.compile": { en: "Bring it all together", es: "Júntalo todo" },
  "cauldron.compiling": { en: "Drawing it together...", es: "Juntándolo todo..." },
  "cauldron.clear": { en: "Empty the Cauldron", es: "Vacía el Caldero" },
  // R69 — THE QUESTION BEFORE THE POT IS EMPTIED. It is deliberately NOT R35's `deleteAskConfirm`
  // sentence: that one ends "This can't be undone", and here nothing is destroyed at all. What a
  // clear costs is the STACK she assembled working by working across rooms, so the sentence names
  // that and says plainly where her workings still are.
  "cauldron.clearAsk": {
    en: "Empty the Cauldron? Everything you stacked comes out of it. Your workings stay where you saved them.",
    es: "¿Vaciar el Caldero? Todo lo que juntaste sale de él. Tus obras siguen donde las guardaste.",
  },
  "cauldron.clearYes": { en: "Empty it", es: "Vaciarlo" },
  "cauldron.clearNo": { en: "Keep it as it is", es: "Dejarlo como está" },
  "cauldron.signIn": { en: "Sign in to work the Cauldron.", es: "Inicia sesión para trabajar el Caldero." },
  "cauldron.failed": {
    en: "Your working could not be drawn together right now.",
    es: "Tu obra no se pudo juntar en este momento.",
  },
  "cauldron.stage.gathering": { en: "Gathering what you sent...", es: "Reuniendo lo que mandaste..." },
  "cauldron.stage.drawing": { en: "Drawing the pieces together...", es: "Uniendo las piezas..." },
  "cauldron.stage.order": { en: "Setting the order of the working...", es: "Poniendo el orden de la obra..." },
  // R26's shared sender. One control, eleven surfaces, so these five rows turn every one of them.
  "cauldron.send": { en: "Send to Cauldron", es: "Mandar al Caldero" },
  "cauldron.sending": { en: "Sending…", es: "Mandando…" },
  "cauldron.sent": { en: "✓ In the Cauldron", es: "✓ En el Caldero" },
  "cauldron.sendSignIn": { en: "Sign in to use the Cauldron.", es: "Inicia sesión para usar el Caldero." },
  "cauldron.sendFailed": { en: "Could not send to the Cauldron.", es: "No se pudo mandar al Caldero." },
  "cauldron.goTo": { en: "Go to the Cauldron", es: "Ve al Caldero" },
  // R50: Lala's seal choice-list, the one surface that reached the pot by hand. Its two refusal
  // sentences are the shared sender's rows above; these four are its own. `cauldron.send` is REUSED
  // for the single-seal button rather than copied (14-B5's ruling on `account.myRoom`).
  "cauldron.seals.eyebrow": { en: "Choose one, or layer several", es: "Elige uno, o superpón varios" },
  "cauldron.seals.layer": { en: "Layer {count} in the Cauldron", es: "Superpón {count} en el Caldero" },
  "cauldron.seals.none": { en: "Select seals to layer", es: "Elige sellos para superponer" },
  "cauldron.seals.download": { en: "Download {name}", es: "Descarga {name}" },

  // ---- 14-B8: WHAT SHE IS WATCHING WHILE SHE WAITS, AND IT RIDES ON EVERY PAGE IN THE HOUSE ------
  //
  // N1-2's follower is mounted ONCE at app level, so a Spanish member who pressed Generate and walked
  // away was watching her paid working in English from every room in the house. The in-room strip is
  // the same words one size larger.
  //
  // THE READING'S NAME IS NOT IN HERE. It is server-composed and rides in as a `{label}` token
  // (14-B3's seam), and the two rows that carry it are SPLIT on that token by
  // `src/components/GenerationLine.tsx` so Spanish is not nailed to English's word order.
  "gen.preparing": { en: "{label} is being prepared", es: "{label} se está preparando" },
  "gen.readyNamed": { en: "{label} is ready", es: "{label} ya está listo" },
  "gen.open": { en: "Open it", es: "Ábrelo" },
  "gen.openShort": { en: "Open", es: "Abrir" },
  "gen.dismiss": { en: "Dismiss", es: "Descartar" },
  "gen.dismissNamed": { en: "Dismiss {label}", es: "Descartar {label}" },
  // TWO WHOLE SENTENCES, not a plural glued onto a shared tail. Spanish agrees the possessive and the
  // verb with the number, so "Your working(s)" cannot be one row with a switch in front of it.
  "gen.queue.noteOne": {
    en: "Your working is being prepared. You are free to roam the house. It lands on this room's shelf the moment it is ready, and it waits for you there if you step away.",
    es: "Tu obra se está preparando. Puedes recorrer la casa con libertad. Llega al estante de este cuarto en cuanto esté lista, y te espera ahí si te alejas.",
  },
  "gen.queue.noteMany": {
    en: "Your workings are being prepared. You are free to roam the house. They land on this room's shelf the moment they are ready, and they wait for you there if you step away.",
    es: "Tus obras se están preparando. Puedes recorrer la casa con libertad. Llegan al estante de este cuarto en cuanto estén listas, y te esperan ahí si te alejas.",
  },
  // The elapsed time itself is `elapsedLabel` in src/lib/labels.ts and is NOT a row: "2m 05s" is the
  // same two abbreviations in both languages, which is what `SAME_IN_BOTH` exists to say out loud.
  "gen.stillWorking": { en: "still working, {time}", es: "aún trabajando, {time}" },
  "gen.readyShort": { en: "ready", es: "lista" },
  "gen.createAnother": { en: "Create another", es: "Crea otra" },

  // ---- 14-B7b (part 2): THE SPELLBOOK AND THE THREE CONJURE DRAWERS ----------------------------
  //
  // The book itself is named on `MR_BOOKS`, so its heading and its cover stay; every page of it
  // turns. Its CATEGORY LIST does not, and that is declared under `spellcat.` below: a category is
  // the `?category=` filter value AND comes back on every spell row the server sends.
  "spell.lede": {
    en: "Open the book. Browse the spells by what you need, or tell me your situation and I will write one for you. Every spell comes from the teachings, with its purpose, herbs, tools, oils, and steps.",
    es: "Abre el libro. Busca los hechizos por lo que necesitas, o cuéntame tu situación y yo te escribo uno. Cada hechizo viene de las enseñanzas, con su propósito, hierbas, herramientas, aceites y pasos.",
  },
  "spell.open": { en: "Open the book", es: "Abre el libro" },
  "spell.openAria": { en: "Open the Spellbook", es: "Abre el libro de hechizos" },
  "spell.contents": { en: "Contents", es: "Contenido" },
  "spell.all": { en: "All", es: "Todos" },
  "spell.emptyCategory": { en: "No spells in this category yet.", es: "Todavía no hay hechizos en esta categoría." },
  // R70 — WHAT THE BOOK SAYS WHEN ITS INDEX DID NOT ARRIVE. Deliberately NOT `spell.emptyCategory`:
  // that one is a statement about the CATALOG, and printed over a book the room could not open it
  // tells her the spells she paid generations for are gone (R60's Card Library, R64's Cauldron).
  "spell.unread": {
    en: "This page could not be turned just now. Nothing written here was lost. Try again in a moment.",
    es: "Esta página no se pudo pasar ahora mismo. No se perdió nada de lo escrito aquí. Inténtalo de nuevo en un momento.",
  },
  "spell.browseFailed": {
    en: "The Spellbook did not answer just now. Nothing written here was lost.",
    es: "El Libro de Hechizos no respondió ahora mismo. No se perdió nada de lo escrito aquí.",
  },
  "spell.make.eyebrow": { en: "Make me a spell", es: "Hazme un hechizo" },
  "spell.make.placeholder": {
    en: "Tell me your situation, and I will write you a spell onto these pages...",
    es: "Cuéntame tu situación, y yo te escribo un hechizo en estas páginas...",
  },
  "spell.name.placeholder": { en: "Name your spell (optional)", es: "Nombra tu hechizo (opcional)" },
  "spell.name.aria": { en: "Name your spell", es: "Nombra tu hechizo" },
  "spell.make.going": { en: "Making your spell...", es: "Haciendo tu hechizo..." },
  "spell.hint": {
    en: "Or choose a spell from the contents on the left.",
    es: "O elige un hechizo del contenido de la izquierda.",
  },
  "spell.needSituation": {
    en: "Tell me what you need a spell for, or pick a category.",
    es: "Dime para qué necesitas un hechizo, o elige una categoría.",
  },
  "spell.signIn": { en: "Sign in to work a spell.", es: "Inicia sesión para trabajar un hechizo." },
  "spell.failed": {
    en: "Your spell could not be prepared right now. Please try again.",
    es: "Tu hechizo no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "spell.stage.need": { en: "Reading your need...", es: "Leyendo lo que necesitas..." },
  "spell.stage.teachings": { en: "Turning to the teachings...", es: "Yendo a las enseñanzas..." },
  "spell.stage.gathering": {
    en: "Gathering herbs, oils, and tools...",
    es: "Reuniendo hierbas, aceites y herramientas...",
  },
  "spell.stage.writing": { en: "Writing your spell...", es: "Escribiendo tu hechizo..." },
  // Where the spell came from. The frame turns and the two book TITLES inside it do not.
  "spell.from.catalog": { en: "From Lala's candle spellbook", es: "Del libro de velas de Lala" },
  "spell.from.curated": { en: "From The Conjure Spell Book", es: "De The Conjure Spell Book" },
  "spell.from.written": { en: "A Conjure Spell", es: "Un hechizo Conjure" },
  "spell.block.layering": { en: "How this one layers", es: "Cómo se capea este" },
  "spell.block.seals": { en: "Spirits & seals", es: "Espíritus y sellos" },
  "spell.block.herbs": { en: "Herbs & ingredients", es: "Hierbas e ingredientes" },
  "spell.block.tools": { en: "Tools", es: "Herramientas" },
  "spell.block.oils": { en: "Oils", es: "Aceites" },
  "spell.block.how": { en: "How to work it", es: "Cómo trabajarlo" },
  "spell.seal.or": { en: "(or)", es: "(o)" },
  "spell.seal.herNote": { en: "her note:", es: "su nota:" },
  "spell.seal.with": { en: "With {name}. {placement}", es: "Con {name}. {placement}" },
  "spell.turnBack": { en: "Turn back", es: "Página anterior" },
  "spell.turnForward": { en: "Turn forward", es: "Página siguiente" },
  "spell.nextPage": { en: "Next page ›", es: "Página siguiente ›" },
  "spell.prevPage": { en: "‹ Previous page", es: "‹ Página anterior" },

  // ---- The three conjure drawers (R37's roster, whose WORDS live here) ----
  //
  // **THE KEYS ARE THE DRAWER'S NAME, NOT THE PART'S.** `chant` and `incantation` are on
  // `SACRED_PARTS`, and this catalog refuses a key filed under one, which is the law working exactly
  // as intended: what a chant IS may never be routed through a translation table. These rows are the
  // framing around the drawer that makes one, so they are filed under its plural tab name.
  "drawer.chants.title": { en: "Speak it into being", es: "Háblalo hasta que sea" },
  "drawer.chants.blurb": {
    en: "A short, repeatable chant for your candle or working, in the laws of the spoken word.",
    es: "Un canto corto y repetible para tu vela o tu obra, en las leyes de la palabra hablada.",
  },
  "drawer.chants.placeholder": {
    en: "What are you calling in? Healing, money running to you, a door opening...",
    es: "¿Qué estás llamando? Sanación, dinero corriendo hacia ti, una puerta que se abre...",
  },
  "drawer.chants.cta": { en: "Give me my chant", es: "Dame mi canto" },
  "drawer.affirmations.title": { en: "Already so", es: "Ya está hecho" },
  "drawer.affirmations.blurb": {
    en: "Affirmations shaped to your situation, present tense and already done, to hold your seal work.",
    es: "Afirmaciones hechas a tu situación, en presente y ya cumplidas, para sostener tu trabajo de sellos.",
  },
  "drawer.affirmations.placeholder": {
    en: "What do you want to feel and live as already true?",
    es: "¿Qué quieres sentir y vivir como ya cierto?",
  },
  "drawer.affirmations.cta": { en: "Give me my affirmations", es: "Dame mis afirmaciones" },
  "drawer.incantations.title": { en: "Make it so", es: "Que así sea" },
  "drawer.incantations.blurb": {
    en: "A commanding incantation that bends the situation, woven with the name and the work.",
    es: "Un encantamiento con mando que dobla la situación, tejido con el nombre y la obra.",
  },
  "drawer.incantations.placeholder": {
    en: "What must happen, and to whom? Name them and the situation...",
    es: "¿Qué tiene que pasar, y a quién? Nómbralos y di la situación...",
  },
  "drawer.incantations.cta": { en: "Give me my incantation", es: "Dame mi encantamiento" },
  "craft.field.situation": { en: "Tell me your situation", es: "Cuéntame tu situación" },
  "craft.field.candle": { en: "Working with a candle? (optional)", es: "¿Trabajas con una vela? (opcional)" },
  "craft.field.candlePlaceholder": { en: "e.g. green money candle", es: "p. ej. vela verde de dinero" },
  "craft.needSituation": { en: "Tell me your situation first.", es: "Cuéntame primero tu situación." },
  "craft.going": { en: "Working...", es: "Trabajando..." },
  "craft.status": { en: "Working your words...", es: "Trabajando tus palabras..." },
  "craft.signIn": { en: "Sign in to craft this.", es: "Inicia sesión para hacer esto." },
  "craft.failed": {
    en: "This could not be prepared right now. Please try again.",
    es: "Esto no se pudo preparar en este momento. Inténtalo de nuevo.",
  },
  "craft.autosaved": { en: "Saved to your room automatically.", es: "Guardado en tu cuarto automáticamente." },

  // ---- 14-B7c: THE SEAL CREATOR ---------------------------------------------------------------
  //
  // **THE FORM IS THE LARGEST IN THE HOUSE AND S2's SUBMISSION IS THE GATE REFERENCE**, so the one
  // thing that must be true of this round is that a translated LABEL never reaches the work order.
  // Every field below sends the MEMBER'S OWN TYPED TEXT, verbatim, and the script picker sends the
  // roster's own labels — `SEAL_LANGUAGES` is `PETITION_DIVINE_NAMES`' shape one size up, because
  // what she taps there is not a label for a value, it is the value: `sealScriptFor` reads it, the
  // resolver is asked for that script, and `ringStringsFor` counts inscriptions with it. So there is
  // no row here for English, Latin, Hebrew, Arabic or Spanish, declared under `sealscript.` below.
  //
  // **AND THE NAMES BOX KEEPS ITS EXAMPLE AS A LITERAL**: Yeyayel, Poiel and El Shaddai are names,
  // and one of them is on `PETITION_DIVINE_NAMES` — a row holding it is a row a translator reaches,
  // which is the exact thing 14(a) forbids and this catalog's own gate refuses.
  "seal.title": { en: "Design your own sacred seal", es: "Diseña tu propio sello sagrado" },
  "seal.lede": {
    en: "Input your intention and any names you choose - your own, angel names, divine names, spiritual names - plus any incantations, verses, chants, or affirmations. Choose the language (you can choose more than one). Choose the colors and visual direction you want. 12th House will generate your sacred seal.",
    es: "Escribe tu intención y los nombres que elijas: el tuyo, nombres de ángeles, nombres divinos, nombres espirituales, además de cualquier encantamiento, versículo, canto o afirmación. Elige el idioma (puedes elegir más de uno). Elige los colores y la dirección visual que quieras. 12th House generará tu sello sagrado.",
  },
  "seal.names.label": { en: "Names to inscribe, one per line (optional)", es: "Nombres para inscribir, uno por línea (opcional)" },
  "seal.names.empty": {
    en: "Leave this empty and I will choose one fitting name. Up to {max}.",
    es: "Déjalo vacío y yo elijo un nombre que sirva. Hasta {max}.",
  },
  "seal.names.over": {
    en: "{count} names. A seal carries up to {max} and stays legible, so take {extra} off.",
    es: "{count} nombres. Un sello lleva hasta {max} y sigue siendo legible, así que quita {extra}.",
  },
  "seal.names.ringFull": {
    en: "{count} names across {scripts} scripts is {strings} inscriptions on one ring, and they stop being legible past {max}. Choose up to {allowed} names, or one script.",
    es: "{count} nombres en {scripts} escrituras son {strings} inscripciones en un solo anillo, y dejan de leerse pasadas {max}. Elige hasta {allowed} nombres, o una sola escritura.",
  },
  "seal.names.collapsedOne": { en: "{count} repeat collapsed.", es: "{count} repetición unida." },
  "seal.names.collapsedMany": { en: "{count} repeats collapsed.", es: "{count} repeticiones unidas." },
  "seal.needSomething": {
    en: "Add at least one name or one affirmation to inscribe.",
    es: "Pon al menos un nombre o una afirmación para inscribir.",
  },
  "seal.overNames": {
    en: "A seal can carry up to {max} names and stay legible. You have {count}.",
    es: "Un sello puede llevar hasta {max} nombres y seguir siendo legible. Tienes {count}.",
  },
  "seal.ringTooFull": {
    en: "Each name is inscribed once per script, so that is {strings} inscriptions on one ring. Choose up to {allowed} names for the scripts you picked, or keep your names and choose one script.",
    es: "Cada nombre se inscribe una vez por escritura, así que son {strings} inscripciones en un solo anillo. Elige hasta {allowed} nombres para las escrituras que marcaste, o quédate con tus nombres y elige una sola escritura.",
  },
  "seal.overAffirmations": {
    en: "You can give a seal up to {max} affirmations. You have {count}.",
    es: "Puedes darle a un sello hasta {max} afirmaciones. Tienes {count}.",
  },
  // HER RULING 2026-08-04: the length refusal the form never had. It names the LINE she has to
  // shorten and how far over it is, because "one of them is too long" over eight lines is a search.
  "seal.affTooLong": {
    en: "One line is too long to inscribe: \"{line}…\". Keep each one under {max} characters. That one is {count}.",
    es: "Una línea es demasiado larga para inscribirla: «{line}…». Mantén cada una por debajo de {max} caracteres. Esa tiene {count}.",
  },
  "seal.script.label": { en: "Sacred script (choose one or more)", es: "Escritura sagrada (elige una o más)" },
  // IT #294: she chose Hebrew here and expected her affirmation translated. The script choice
  // writes the SACRED NAMES (the resolver's real scholarship); her own words are inscribed
  // VERBATIM by the owner's ruling (seal-submission.js, "affirmation-missing-or-altered") and are
  // never rewritten by a machine. The form now says so instead of leaving her to find out.
  "seal.script.hint": {
    en: "These scripts write the sacred names on the seal. Your affirmations are inscribed exactly as you write them, in whichever language you write them.",
    es: "Estas escrituras escriben los nombres sagrados en el sello. Tus afirmaciones se inscriben exactamente como las escribes, en el idioma en que las escribas.",
  },
  "seal.aff.label": {
    en: "Affirmations, incantations, or verses, one per line (optional)",
    es: "Afirmaciones, encantamientos o versículos, uno por línea (opcional)",
  },
  "seal.aff.placeholder": {
    en: "I am wealthy\nI am favored in every room I walk into",
    es: "Soy próspera\nTengo el favor en cada cuarto donde entro",
  },
  "seal.aff.empty": {
    en: "Your own words, exactly as you write them. Up to {max}.",
    es: "Tus propias palabras, tal como las escribes. Hasta {max}.",
  },
  "seal.symbols.label": { en: "Symbols to include (optional)", es: "Símbolos para incluir (opcional)" },
  "seal.symbols.placeholder": {
    en: "e.g. the symbol for Jupiter, a pentagram. Leave empty for a pure design.",
    es: "p. ej. el símbolo de Júpiter, un pentagrama. Déjalo vacío para un diseño puro.",
  },
  "seal.style.label": { en: "Colors or visual direction (optional)", es: "Colores o dirección visual (opcional)" },
  "seal.style.placeholder": {
    en: "Any colors you want (the background stays white). Leave empty and I will choose.",
    es: "Los colores que quieras (el fondo se queda blanco). Déjalo vacío y yo elijo.",
  },
  "seal.go": { en: "Create My Seal", es: "Crea mi sello" },
  "seal.going": { en: "Creating your seal...", es: "Creando tu sello..." },
  "seal.runAgain": { en: "Run it again", es: "Córrelo otra vez" },
  "seal.signIn": { en: "Sign in to create your seal.", es: "Inicia sesión para crear tu sello." },
  "seal.stage.preparing": { en: "Preparing your seal...", es: "Preparando tu sello..." },
  "seal.stage.names": { en: "Choosing the sacred names...", es: "Eligiendo los nombres sagrados..." },
  "seal.stage.drawing": { en: "Drawing your seal...", es: "Dibujando tu sello..." },
  "seal.stage.inscribing": { en: "Inscribing every sacred name...", es: "Inscribiendo cada nombre sagrado..." },
  "seal.stage.checking": { en: "Checking each name and detail...", es: "Revisando cada nombre y cada detalle..." },
  "seal.reworkingStatus": {
    en: "Reworking your seal with your change...",
    es: "Rehaciendo tu sello con tu cambio...",
  },
  "seal.slow": {
    en: "Your seal is taking longer than usual. Nothing was lost: your details are saved, and one tap runs it fresh.",
    es: "Tu sello está tardando más de lo normal. No se perdió nada: tus datos están guardados, y con un toque se corre de nuevo.",
  },
  "seal.lost": {
    en: "Your seal could not be completed this time. Nothing was lost: your details are saved, and one tap runs it fresh.",
    es: "Tu sello no se pudo completar esta vez. No se perdió nada: tus datos están guardados, y con un toque se corre de nuevo.",
  },
  // R217 — THE POLL GAVE UP AND THE SEAL DID NOT. `seal.slow` above says "one tap runs it fresh",
  // and running it fresh is the one thing that must not happen while the pipeline is still drawing
  // her seal: it is a second paid render. These two are what the room says and offers instead, and
  // they are reached ONLY when the server still has her seal in flight.
  "seal.stillDrawing": {
    en: "Your seal is taking longer than usual, and it is still being drawn. Nothing was lost: pick the wait back up and it will appear here.",
    es: "Tu sello está tardando más de lo normal, y todavía se está dibujando. No se perdió nada: retoma la espera y aparecerá aquí.",
  },
  "seal.keepWaiting": { en: "Pick the wait back up", es: "Retomar la espera" },
  // R63: the house's own name for a seal that arrived without one, and the names the server sends
  // back with every refusal — which the room dropped on the floor until this round.
  "seal.defaultTitle": { en: "Your Seal", es: "Tu sello" },
  "seal.namesHeld": {
    en: "Your names are resolved and saved: {names}",
    es: "Tus nombres están resueltos y guardados: {names}",
  },
  "seal.zoom": { en: "Zoom seal", es: "Ampliar el sello" },
  "seal.downloadImage": { en: "Download seal PDF", es: "Descargar el PDF del sello" },
  "seal.downloadDescription": { en: "Download description PDF", es: "Descargar el PDF de la descripción" },
  "seal.edit.label": {
    en: "Want a change? Tell me and I will rework it",
    es: "¿Quieres un cambio? Dímelo y lo rehago",
  },
  "seal.edit.placeholder": {
    en: "e.g. thicker ring, remove the moon, more gold",
    es: "p. ej. anillo más grueso, quita la luna, más dorado",
  },
  "seal.edit.go": { en: "Regenerate", es: "Rehacer" },
  "seal.edit.going": { en: "Reworking...", es: "Rehaciendo..." },
  "seal.meaning": { en: "What your seal means", es: "Lo que significa tu sello" },
  "seal.savedNote": { en: "✓ Saved to your room.", es: "✓ Guardado en tu cuarto." },

  // ---- The switcher (ITEM 14c) ----
  //
  // **S7-2, HER RULING (2026-08-05): THE PROSE IS GONE AND THE ROW WENT WITH IT.** `language.note`
  // said *"The house speaks to you in this. The words of a working are never translated."* — a
  // brochure line above a two-chip control. **The sacred-verbatim LAW itself is UNTOUCHED**: it lives
  // in `src/language.js` and is enforced by the engine, which is where it has teeth. A sentence in a
  // dropdown was never what kept a psalm from being paraphrased.
  //
  // The row is DELETED rather than blanked, because `item14-strings.mjs` holds that every row is READ
  // by some surface and a row nobody reads is exactly what that fixture goes red about.
  "language.heading": { en: "Your language", es: "Tu idioma" },
  "language.choose": { en: "Switch to {language}", es: "Cambiar a {language}" },

  // ---- P4: NOTIFICATIONS, IN THE SAME MENU AND FOR THE SAME REASON AS THE SWITCHER -------------
  //
  // One row per state of `PUSH_PANEL_STATES` — **and after S7-1 only three of the six carry a second
  // line at all.**
  //
  // **HER RULING (2026-08-05): THE EXPLAINER DIES.** *"This device gets a nudge when a reading you
  // ask for is ready and when someone answers you"* is gone from ON and from OFF, and the waiting
  // state's *"One moment."* went with it — **it was wordy AND it promised a scope the house has
  // already outgrown** (the room, the morning line and the bell all send now). The panel is the
  // TITLE, the HONEST STATE and the ONE BUTTON matching it.
  //
  // **WHAT SURVIVES IS A REPAIR RATHER THAN AN EXPLANATION, and that is the line her ruling draws:**
  // the three states with no button are the three where the fix is somewhere else — device settings,
  // the home screen, another browser — so each keeps ONE line saying where. A blocked member and an
  // uninstalled iPhone are both "not receiving" and the repairs are completely different, which is
  // why one shared sentence would be useless to both and why the roster is six rather than three.
  // All three are trimmed to one sentence: the TITLE already says what the state is.
  // P3 — **THE STANDING SMS DOOR, IN THE SAME MENU AND THEREFORE IN THE SAME LANGUAGE.** Its
  // neighbour `PushSettings` resolves every word through `t`, and an English-only panel dropped
  // into a translated menu is the inconsistency a member actually sees. **The one thing that stays
  // raw English is the CONSENT SENTENCE**, imported from `src/memberPhone.js`: it is one
  // declaration read by all three doors, and three doors promising three different things is exactly
  // what that declaration exists to make impossible (R28/R29). Said out loud rather than left as an
  // absence (R17/R160) — translating a carrier-compliance line is a copy decision and is HERS.
  //
  // The FIELD reuses `signup.phonePlaceholder`, which is already on `SAME_IN_BOTH` with its
  // reason: the shape of a dialable number is one answer, not one per door.
  // ---- INSTALL-1, HER CLAUSES 1 AND 6 (2026-08-17) -------------------------------------------
  //
  // **CLAUSE 1 — THE ONE-LINE ASK.** The retired copy carried a title, a two-clause body, a lead, a
  // why-it-matters line and a step list, and a member met all of it at once on a phone. Her clause
  // is ONE LINE: what it is, in a sentence, and then the route. The `why` survives as the SECOND
  // half of that one line rather than as a paragraph of its own, because the reason it exists —
  // notifications only work from the home-screen app on an iPhone — is the whole persuasion.
  //
  // **CLAUSE 6 — BOTH LANGUAGES.** The copy was raw English in `src/installPrompt.js` and its own
  // comment flagged it as a placeholder awaiting her word. This is her word. It lives here now,
  // which is where ITEM 14b's census can see it and hold both halves.
  "install.ask": {
    en: "Add 12th House to your home screen: it is the only way we can tell you when a reading of yours is ready.",
    es: "Añade 12th House a tu pantalla de inicio: es la única forma de avisarte cuando una lectura tuya esté lista.",
  },
  "install.action": { en: "Add it", es: "Añadir" },
  "install.later": { en: "Not now", es: "Ahora no" },
  "install.iosStep1": {
    en: "Tap the Share icon in Safari, a square with an arrow pointing up. If you cannot see it, scroll up to bring the toolbar back.",
    es: "Toca el icono Compartir en Safari, un cuadrado con una flecha hacia arriba. Si no lo ves, desliza hacia arriba para que vuelva la barra.",
  },
  "install.iosStep2": {
    en: "Scroll down and tap Add to Home Screen.",
    es: "Desplázate hacia abajo y toca Añadir a pantalla de inicio.",
  },
  "install.androidStep1": {
    en: "Tap the three-dot menu at the top right of Chrome.",
    es: "Toca el menú de tres puntos arriba a la derecha de Chrome.",
  },
  "install.androidStep2": {
    en: "Tap Install app, or Add to Home screen.",
    es: "Toca Instalar aplicación, o Añadir a pantalla de inicio.",
  },
  // INSTALL-1 CLAUSE 7 — the way back. It says what it DOES rather than naming the machinery
  // (S1-A/R117: a member does not have an `install_answer`, she has a home screen), and the
  // installed line is a STATEMENT rather than a dead control, because a button that cannot
  // change anything is A2's dead box (ITEM A's own no-dead-read-only-lists clause).
  "install.reofferHeading": { en: "Home screen", es: "Pantalla de inicio" },
  // >>> HER ART-DIRECTION PASS, CLAUSE 5: *"label it simply 'Add to home screen'."* <<< The retired
  // wording ("Show me how again") described the SHEET the tap opens rather than the thing it gets
  // her, which is S1-A/R117's own rule about naming the act instead of the machinery -- and it read
  // as a plea on a control she has already dismissed once. REPLACED at its declaration rather than
  // a second row added beside it (DL-2/DT-2b/R181), so no reader can find the retired sentence and
  // assume it is still wanted.
  "install.reofferAction": { en: "Add to home screen", es: "Añadir a la pantalla de inicio" },
  "install.reofferDone": {
    en: "12th House is on your home screen.",
    es: "12th House está en tu pantalla de inicio.",
  },
  "install.reofferFailed": {
    en: "That could not be reset just now. Try again in a moment.",
    es: "No se pudo restablecer ahora mismo. Inténtalo de nuevo en un momento.",
  },
  "sms.heading": { en: "Text messages", es: "Mensajes de texto" },
  "sms.checking": { en: "Checking your account", es: "Comprobando tu cuenta" },
  "sms.none": { en: "No number on file", es: "No hay ningún número en tu cuenta" },
  // NUMBER-ONLY is a real, reachable state: both the signup field and the popup carry an unchecked
  // opt-in that never blocks the save, so a member can have a number here and be in no audience.
  // The line says both halves, because a panel that reported her as ON would be telling her about
  // texts that are not coming.
  "sms.numberOnly": {
    en: "Your number is on file. Texts from the house are off.",
    es: "Tu número está en tu cuenta. Los mensajes de la casa están desactivados.",
  },
  "sms.on": {
    en: "Your number is on file. Texts from the house are on.",
    es: "Tu número está en tu cuenta. Los mensajes de la casa están activados.",
  },
  "sms.field": { en: "Phone number", es: "Número de teléfono" },
  "sms.save": { en: "Save my number", es: "Guardar mi número" },
  "sms.update": { en: "Update my number", es: "Actualizar mi número" },
  "sms.remove": { en: "Remove my number", es: "Quitar mi número" },
  "sms.working": { en: "One moment...", es: "Un momento..." },
  "sms.failed": {
    en: "That did not save just now. Try again in a moment.",
    es: "Eso no se guardó ahora mismo. Inténtalo de nuevo en un momento.",
  },
  "push.heading": { en: "Notifications", es: "Notificaciones" },
  "push.checking": { en: "Checking this device", es: "Comprobando este dispositivo" },
  "push.on": { en: "Notifications are on", es: "Las notificaciones están activadas" },
  "push.off": { en: "Notifications are off", es: "Las notificaciones están desactivadas" },
  // The one state she cannot act on from this screen, so it says where she CAN act. No site is
  // allowed to raise the browser's ask again after a denial, and a control offering to try would be
  // a moment that cannot complete (P4-4's own ruling).
  "push.blocked": { en: "Notifications are blocked", es: "Las notificaciones están bloqueadas" },
  "push.blockedNote": {
    en: "No site can ask again after that, so turn them back on in your device settings, under 12th House.",
    es: "Ningún sitio puede volver a preguntar, así que actívalas en los ajustes de tu dispositivo, en 12th House.",
  },
  "push.needsInstall": { en: "Add 12th House to your home screen", es: "Añade 12th House a tu pantalla de inicio" },
  "push.needsInstallNote": {
    en: "Tap Share, then Add to Home Screen, and open 12th House from there.",
    es: "Toca Compartir, luego Añadir a pantalla de inicio, y abre 12th House desde ahí.",
  },
  "push.unsupported": { en: "This browser cannot send notifications", es: "Este navegador no puede enviar notificaciones" },
  "push.unsupportedNote": {
    en: "Open 12th House on your phone, or in a browser that supports them, and the choice will be here.",
    es: "Abre 12th House en tu teléfono, o en un navegador que las admita, y la opción estará aquí.",
  },
  // IT #179 — **THE MORNING LINE'S OWN SWITCH, AND IT SITS BESIDE NOTIFICATIONS RATHER THAN INSIDE
  // THEM.** sshinetha asked to stop the daily guidance and the only exit this house had was the
  // notifications door, which would also have stopped her hearing that someone messaged her. The
  // copy says WHICH thing this governs in its first line, because a member who reads "Off" and then
  // stops getting messages will not blame this control (R18).
  "daily.setting.heading": { en: "Your morning line", es: "Tu mensaje de la mañana" },
  "daily.setting.on": { en: "Lala sends you a reading each morning", es: "Lala te envía una lectura cada mañana" },
  "daily.setting.off": { en: "Your morning reading is off", es: "Tu lectura de la mañana está desactivada" },
  "daily.setting.unknown": { en: "Checking your setting", es: "Comprobando tu preferencia" },
  "daily.setting.note": {
    en: "This is only the morning reading. Your other notifications are not affected.",
    es: "Esto es solo la lectura de la mañana. Tus otras notificaciones no cambian.",
  },
  "daily.setting.turnOn": { en: "Turn on", es: "Activar" },
  "daily.setting.turnOff": { en: "Turn off", es: "Desactivar" },

  // LALA-BRAIN-1 CLAUSE 2 — her journal's own door, beside the morning line's because they are the
  // same KIND of decision: what this house may do with her, one feature at a time.
  "journal.lala.heading": { en: "Your journal and Lala", es: "Tu diario y Lala" },
  "journal.lala.on": { en: "Lala can read your journal when it helps her answer you", es: "Lala puede leer tu diario cuando le ayuda a responderte" },
  "journal.lala.off": { en: "Your journal is private from Lala", es: "Tu diario es privado y Lala no lo lee" },
  "journal.lala.unknown": { en: "Checking your setting", es: "Comprobando tu preferencia" },
  "journal.lala.note": {
    en: "This only changes whether Lala may read it. Nothing is deleted, and your journal stays exactly where it is.",
    es: "Esto solo cambia si Lala puede leerlo. No se borra nada y tu diario se queda donde está.",
  },
  "journal.lala.share": { en: "Let Lala read it", es: "Dejar que Lala lo lea" },
  "journal.lala.keepPrivate": { en: "Keep it private", es: "Mantenerlo privado" },
  "journal.lala.working": { en: "Saving", es: "Guardando" },
  "journal.lala.failed": { en: "That did not save. Try again in a moment.", es: "No se guardó. Inténtalo de nuevo en un momento." },
  "daily.setting.working": { en: "One moment...", es: "Un momento..." },
  "daily.setting.failed": {
    en: "That did not go through. Try again in a moment.",
    es: "Eso no se guardó ahora mismo. Inténtalo de nuevo en un momento.",
  },
  "push.turnOn": { en: "Turn on", es: "Activar" },
  "push.turnOff": { en: "Turn off", es: "Desactivar" },
  "push.working": { en: "One moment...", es: "Un momento..." },
  // R47/R53/R61's law on a control whose whole job is a round trip: a refusal SAYS SO, and it says
  // what to do next rather than only that something went wrong.
  "push.failed": {
    en: "That did not go through. Try again in a moment.",
    es: "No se pudo completar. Inténtalo de nuevo en un momento.",
  },

  // ---- 14-B7d(1): THE LIBRARY'S CHROME, AND THE LINE IS BETWEEN A DOOR AND A CATALOGUE ----------
  //
  // The room's own name is `brand.` (14-B5) and stays. What turns is everything she operates: the
  // two view tabs, the search, the jump menu and the five sentences an empty shelf can say.
  //
  // **WHAT A SHELF IS CALLED IS DATA, NOT COPY, AND THAT IS THIS ROUND'S BOUNDARY.** The plates on
  // the Library's shelves are the `categories` column the ingest wrote (Tarot, Symbols, Sacred
  // Sexuality, African Diaspora, Alchemy, Astrology, Egyptian) — they arrive per book from
  // `/api/library/books`, they are what the filter chips scroll to, and they are her open scope
  // question along with the book titles themselves. So the plate takes its words from the data and
  // the FRAME around it takes them from here: `{name}` is the category, and the sentence it sits in
  // is the house speaking.
  "library.books": { en: "Library books", es: "Libros de la biblioteca" },
  "library.view": { en: "Library view", es: "Vista de la biblioteca" },
  "library.tab.video": { en: "Video Library", es: "Biblioteca de videos" },
  "library.tab.book": { en: "Book Library", es: "Biblioteca de libros" },
  "library.search.placeholder": {
    en: "Search books, topics, rituals...",
    es: "Busca libros, temas, rituales...",
  },
  "library.search.label": { en: "Search the library", es: "Busca en la biblioteca" },
  "library.jump": { en: "Jump to a shelf", es: "Ir a un estante" },
  // R3's law: this counts TITLES and it says so. The plural is TWO rows, because Spanish agrees the
  // participle with the noun and a `{s}` token cannot carry that.
  "library.titled.one": {
    en: "{count} book titled “{term}”",
    es: "{count} libro titulado “{term}”",
  },
  "library.titled.many": {
    en: "{count} books titled “{term}”",
    es: "{count} libros titulados “{term}”",
  },
  // R109: the same count, on the tab where the things being counted are not books. The search box is
  // ONE input serving both tabs, so this line has said "0 books titled …" over a wall of videos since
  // the toggle shipped. Deliberately NOT a "{noun}" token: Spanish agrees the article and the number
  // with the noun, so a glued-in word is a sentence this catalog cannot translate (14-B5's ruling on
  // the bell's joined string).
  "library.titled.videoOne": {
    en: "{count} video titled “{term}”",
    es: "{count} video titulado “{term}”",
  },
  "library.titled.videoMany": {
    en: "{count} videos titled “{term}”",
    es: "{count} videos titulados “{term}”",
  },
  // T8-9: a shelf that FAILED TO LOAD and an empty shelf are not the same sentence, and R3 added a
  // third — a title search that matched nothing while the PAGES matched plenty.
  "library.empty.error": {
    en: "The shelves could not be loaded just now. Refresh and they will come back.",
    es: "Los estantes no se pudieron cargar ahora mismo. Actualiza y volverán.",
  },
  "library.empty.video": {
    en: "The Video Library is ready. Class videos will appear here on their own shelves once they're added.",
    es: "La biblioteca de videos está lista. Los videos de las clases aparecerán aquí en sus propios estantes en cuanto se agreguen.",
  },
  "library.empty.titledWithPassages": {
    en: "No book is titled “{term}”, but the passages above are inside the books.",
    es: "Ningún libro se titula “{term}”, pero los pasajes de arriba están dentro de los libros.",
  },
  "library.empty.titled": {
    en: "No book is titled “{term}”.",
    es: "Ningún libro se titula “{term}”.",
  },
  // R109 — **R3's SENTENCE, ONE MEDIA OVER, AND IT COULD NEVER BE REACHED.** R3 wrote the two search
  // arms BELOW the media arm, so a filtered VIDEO shelf fell through to "Class videos will appear
  // here on their own shelves once they're added" — the app telling her the library is empty while
  // her own search is what emptied it. The key is `videoTitled` rather than `video.titled` on
  // purpose: `library.empty.video` would be a PREFIX of it, and a prefix is not a different key.
  "library.empty.videoTitled": {
    en: "No video is titled “{term}”.",
    es: "Ningún video se titula “{term}”.",
  },
  "library.empty.none": { en: "No books on the shelves yet.", es: "Aún no hay libros en los estantes." },
  // R85 — **ITS OWN SENTENCE, DELIBERATELY NOT `library.empty.error`.** Lala's Bookshelf is a band of
  // its own above the catalogue, read from its own route, and it was drawn only when the list came
  // back non-empty — so a failed read removed the whole shelf with nothing red anywhere (R58's absent
  // section). Saying "the shelves could not be loaded" over a catalogue that loaded perfectly well is
  // a wrong sentence about shelves she can see (R64/R70).
  "library.featured.failed": {
    en: "Lala’s Bookshelf could not be loaded just now. Refresh and her books come back.",
    es: "La estantería de Lala no se pudo cargar ahora mismo. Actualiza y sus libros vuelven.",
  },

  // The shelf shell's own accessible names. `{name}` is the plate — a category from the catalogue,
  // or, in My Room, the house's own word for her saved shelf.
  "library.shelf.books": { en: "{name} shelf", es: "Estante de {name}" },
  "library.shelf.videos": { en: "{name} videos", es: "Videos de {name}" },
  "library.shelf.slideLeft": {
    en: "Slide {name} shelf left",
    es: "Deslizar el estante de {name} hacia la izquierda",
  },
  "library.shelf.slideRight": {
    en: "Slide {name} shelf right",
    es: "Deslizar el estante de {name} hacia la derecha",
  },
  "library.shelf.slideVideosLeft": {
    en: "Slide {name} left",
    es: "Deslizar {name} hacia la izquierda",
  },
  "library.shelf.slideVideosRight": {
    en: "Slide {name} right",
    es: "Deslizar {name} hacia la derecha",
  },

  // **THE ONE PLATE THAT IS THE HOUSE'S OWN WORD, AND IT IS WHY THIS ROUND EXISTS.** `savedshelf.`
  // was declared ENGLISH BY DESIGN with a reason that ended "the plate turns when the Library does",
  // because the string she reads was ALSO the DOM id (`shelfSlug`) and rode into English accessible
  // names. It is two props now: `category` is the id and the data key, `label` is what is painted.
  "savedshelf.plate": { en: "Saved Shelf", es: "Estante guardado" },

  // ---- 14-B7d(2): BOTH READERS, THE BOOKMARK BAR, THE HIT LIST AND THE SAVE CHIP ---------------
  //
  // **THE BOOK'S OWN WORDS ARE NOT IN HERE AND NEVER WILL BE** — the title, the author, the
  // paragraphs. That is her original scope question, and this round is everything the HOUSE says
  // around them: the controls that turn a page, size the text, search inside it, mark a place and
  // put the book on her shelf. `{title}` and `{book}` are her book's own name riding INTO a sentence
  // rather than being glued in front of it, which is the same reason `{name}` exists one block up.
  "reader.modalLabel": { en: "{title} reader", es: "Lector de {title}" },
  "reader.close": { en: "Close reader", es: "Cerrar el lector" },
  "reader.prevPage": { en: "Previous page", es: "Página anterior" },
  "reader.nextPage": { en: "Next page", es: "Página siguiente" },
  "reader.prev": { en: "Previous", es: "Anterior" },
  "reader.next": { en: "Next", es: "Siguiente" },
  "reader.pageOf": { en: "Page {page} of {total}", es: "Página {page} de {total}" },
  "reader.pageAlt": { en: "{title}, page {page}", es: "{title}, página {page}" },
  "reader.textSize": { en: "Text size", es: "Tamaño del texto" },
  "reader.smaller": { en: "Smaller text", es: "Texto más pequeño" },
  "reader.larger": { en: "Larger text", es: "Texto más grande" },
  // A long title is replaced by "this book" so the box does not clip; the swap is the ROOM's, and
  // both halves are rows so the substitution reads the same in either language.
  "reader.searchInside": { en: "Search inside {book}...", es: "Busca dentro de {book}..." },
  "reader.searchInsideLabel": { en: "Search inside {book}", es: "Busca dentro de {book}" },
  "reader.thisBook": { en: "this book", es: "este libro" },
  "reader.signIn": { en: "Sign in to open this book.", es: "Inicia sesión para abrir este libro." },
  "reader.opening": { en: "Opening…", es: "Abriendo…" },
  "reader.jumpPlaceholder": { en: "Page #", es: "N.º de página" },
  "reader.jumpLabel": { en: "Jump to page", es: "Ir a la página" },
  "reader.go": { en: "Go", es: "Ir" },
  // W1-6b's card: what the reader shows when a book's text is not ingested yet. It says the one
  // thing that matters to somebody standing in front of it — that the rest of the room still works.
  "reader.pendingLead": {
    en: "This book is still being prepared.",
    es: "Este libro todavía se está preparando.",
  },
  "reader.pendingNote": {
    en: "The full text of {title} is being set for reading and will open here shortly. Your bookmarks and your saved shelf keep working in the meantime.",
    es: "El texto completo de {title} se está preparando para la lectura y se abrirá aquí en breve. Mientras tanto, tus marcadores y tu estante guardado siguen funcionando.",
  },

  // W2-1's loop, said in her language: bottom of the open book, name it, save it, find it in My Room.
  "bookmark.prompt": { en: "Bookmark this page.", es: "Marca esta página." },
  "bookmark.namePlaceholder": {
    en: "Name this bookmark (page {page})",
    es: "Nombra este marcador (página {page})",
  },
  // Save and Cancel are `common.save` / `common.cancel` — 14-B6b deleted two rows saying what
  // another row already said, and the bookmark bar is not a special case of either word.
  "bookmark.goToPage": { en: "Go to page {page}", es: "Ir a la página {page}" },
  // R61 — the three sentences a bookmark write says when it did not go. Until this round both
  // writes ended in `.catch(() => {})`, so a member named a mark, watched the form close, and had
  // nothing. `signIn` is the one refusal she can fix in a single tap and the one she was never told.
  "bookmark.signIn": { en: "Sign in to bookmark this page.", es: "Inicia sesión para marcar esta página." },
  "bookmark.saveFailed": {
    en: "This bookmark could not be saved just now. Try again.",
    es: "Este marcador no se pudo guardar ahora mismo. Inténtalo de nuevo.",
  },
  "bookmark.removeFailed": {
    en: "This bookmark could not be removed just now.",
    es: "Este marcador no se pudo quitar ahora mismo.",
  },

  // R3's six states, each owing her a different sentence. The count and the noun are FOUR rows, not
  // a `{s}` token: Spanish agrees the article with the noun, and "the book" / "the books" is a
  // second axis on the same sentence (the reader searches one, the library searches all of them).
  "hits.searching": { en: "Searching the pages...", es: "Buscando en las páginas..." },
  "hits.signIn": {
    en: "Sign in to search inside the books.",
    es: "Inicia sesión para buscar dentro de los libros.",
  },
  "hits.none": {
    en: "Nothing inside the pages for “{term}”.",
    es: "No hay nada dentro de las páginas para “{term}”.",
  },
  // R93: the two failures that used to wear "Nothing inside the pages" — one is "we could not ask",
  // the other is "there is nothing here to ask of", and telling her to retry the second would be
  // advice that cannot work.
  "hits.failed": {
    en: "The search did not answer just then. Try that once more.",
    es: "La búsqueda no respondió en ese momento. Inténtalo una vez más.",
  },
  "hits.unavailable": {
    en: "The pages are not searchable right now.",
    es: "Las páginas no se pueden buscar en este momento.",
  },
  "hits.oneInBook": { en: "{count} passage inside the book", es: "{count} pasaje dentro del libro" },
  "hits.manyInBook": { en: "{count} passages inside the book", es: "{count} pasajes dentro del libro" },
  "hits.oneInBooks": { en: "{count} passage inside the books", es: "{count} pasaje dentro de los libros" },
  "hits.manyInBooks": { en: "{count} passages inside the books", es: "{count} pasajes dentro de los libros" },
  "hits.page": { en: "Page {page}", es: "Página {page}" },

  // W5-2's one save, four surfaces. The chip has room for a mark, so its words are its accessible
  // name — and `{title}` rides inside the sentence rather than in front of it.
  "save.saved": { en: "✓ Saved to My Room", es: "✓ Guardado en Mi Cuarto" },
  "save.add": { en: "＋ Save to My Room", es: "＋ Guardar en Mi Cuarto" },
  "save.ariaSaved": { en: "{title} is saved to My Room", es: "{title} está guardado en Mi Cuarto" },
  "save.ariaAdd": { en: "Save {title} to My Room", es: "Guardar {title} en Mi Cuarto" },

  // ---- 14-B7d(3): THE VIDEO HALF OF THE LIBRARY -----------------------------------------------
  //
  // T8-9's ruling is what these rows have to keep true: **the shelf shows EVERY tier what is there**,
  // so a card is never removed and never disabled, and it carries its own state — a PRICE when it is
  // a one-time product she has not bought, a LOCK when playback needs a tier she is not on, the
  // plain play face when it is hers. All three of those are accessible names, and an accessible name
  // is the one kind of copy nobody looking at the screen can see is still English.
  //
  // **WHAT THE SERVER COMPOSED PASSES THROUGH UNTOUCHED** (14-B3's seam): the product's own blurb,
  // its `priceText`, and the `message` a refused checkout sends back. Re-translating a price or a
  // refusal on the client is a second answer key on the screen where being wrong costs money.
  "video.play": { en: "Play {title}", es: "Reproducir {title}" },
  // The em dash this catalog forbids outright was in the original ("{title} — locked"), so the
  // English is a comma now. Nothing else about the sentence changed.
  "video.locked": { en: "{title}, locked", es: "{title}, bloqueado" },
  "video.get": { en: "Get {title}", es: "Consigue {title}" },
  "video.coverLabel": { en: "{title} cover", es: "Portada de {title}" },
  "video.playerLabel": { en: "{title} video player", es: "Reproductor de video de {title}" },
  "video.closeVideo": { en: "Close video", es: "Cerrar el video" },
  "video.playVideo": { en: "▶ Play video", es: "▶ Reproducir video" },
  // T1-7 — SPIRIT KEEPING: bought once, hers for good, and the box says so BEFORE she pays.
  "video.keep": {
    en: "A single purchase. It stays with your account for good, whatever your membership does later.",
    es: "Una sola compra. Se queda en tu cuenta para siempre, hagas lo que hagas con tu membresía después.",
  },
  "video.owned": { en: "Yours, for good.", es: "Tuyo, para siempre." },
  "video.getFor": { en: "Get it for {price}", es: "Consíguelo por {price}" },
  "video.openingCheckout": { en: "Opening checkout...", es: "Abriendo el pago..." },
  "video.checkoutFailed": {
    en: "Checkout could not be opened just now. Try again in a moment.",
    es: "El pago no se pudo abrir en este momento. Vuelve a intentarlo en un momento.",
  },
  "video.progressNote": {
    en: "Your place is saved as you watch. Pick it back up from your room.",
    es: "Tu lugar se guarda mientras miras. Retómalo desde tu cuarto.",
  },
  // R55 — **THE LINE ABOVE IS A PROMISE, AND IT WENT ON MAKING IT WHILE EVERY SAVE WAS REFUSED.**
  // The player drained the write's answer to text and threw it away, so a whole session of watching
  // could go unrecorded under a sentence saying it was being kept. This one REPLACES the promise
  // when the server says no, and disappears again the moment a save lands. A progress ping is not a
  // tap she is waiting on, so a success stays silent; only the promise being false is worth a word.
  "video.progressFailed": {
    en: "Your place is not being saved right now. Note where you are before you close this.",
    es: "Tu lugar no se está guardando ahora mismo. Anota dónde vas antes de cerrar esto.",
  },
};

// **THE EXCEPTIONS, DECLARED BY NAME.** 14(b) shipped this roster EMPTY, with its reason; 14-B2 is
// the round that populated it, and the two entries are the honest kind rather than the tired kind.
// "Hr" and "Min" are the SAME ABBREVIATION in both languages (hora/minuto), and they sit inside a
// three-select time row where the abbreviation is what makes it fit. Declaring them is what makes
// "deliberately the same" a different fact from "nobody got to this key" — which is the whole reason
// an identical value is red in the first place. R43's M8 still applies: the gate does not merely walk
// this list (a filter over a short roster proves little), it drives the law over probe catalogs both
// directions and holds this roster to naming only keys that exist.
//
// 14-B4 added two of the other honest kind: **"Tarot" and "Videos" are the same word in Spanish**,
// and they are panel eyebrows sitting directly above titles that DO turn ("Tus cartas", "Seguir
// viendo"), so the pair reads as translated rather than as forgotten. A proper noun and a loan word
// are the two things this roster is actually for.
//
// 14-B6c added the first one that is not a word at all: the astrocartography search box's example
// is A REAL PLACE typed into a geocoder. "Los Ángeles, California, Estados Unidos" is a lookup that
// finds nothing, so the example stays exactly as it is — and it is DECLARED, because an identical
// value is otherwise indistinguishable from a row nobody translated.
export const SAME_IN_BOTH = ["time.hourShort", "time.minuteShort", "panel.tarot.eyebrow", "panel.videos.eyebrow",
  // Her menu link: "12th House Truth" is the wing's own name, a proper noun in every language.
  "account.truth",
  // C8b — the phone placeholder is a NUMBER FORMAT rather than words: `(555) 123-4567` is the shape
  // of the plan the declared default country code accepts, and it is the same shape whichever
  // language the form is read in. Translating punctuation would be inventing a second answer to what
  // a valid number looks like on the one field the SMS rail is fed from.
  "signup.phonePlaceholder",
  "acg.tools.placeholder",
  // HOUSE READINGS (e): Check a Place's example is a real place too, for the same reason.
  "acg.check.placeholder",
  // Her membership lander names eighteen offerings one by one, and TAROT is the same word in
  // Spanish. Translating it would be inventing a second answer to what the practice is called.
  "membership.lander.o.tarot.title"];

// ---------------------------------------------------------------------------------------------
// **WHAT IS DELIBERATELY STILL IN ENGLISH, AND WHY — DECLARED, NEVER LEFT AS AN ABSENCE.**
//
// R28/R29/R30's fix shape one level up: the exceptions to the catalog are a ROSTER with reasons
// rather than the set of keys nobody happened to write. An untranslated surface that is declared is
// a decision somebody can overturn; an untranslated surface that is merely missing is indistinguishable
// from an oversight, and it is the oversight this whole layer exists to make visible.
//
// The law below refuses any key filed under a declared prefix, so the two cannot drift apart: the day
// a key is written for one of these surfaces, either the reason is deleted or the gate goes red.
// ---------------------------------------------------------------------------------------------
export const ENGLISH_BY_DESIGN = [
  {
    surface: "the consent block on signup",
    keyPrefix: "consent.",
    reason:
      "The sentence beside that checkbox IS the record. `src/termsConsent.js` owns it (CONSENT_TEXT), "
      + "the server records the version she agreed to against her row, and both documents behind it are "
      + "English. Translating the sentence without the documents tells a Spanish reader she has read "
      + "something she cannot read, and records an English text she never saw. The documents come first.",
  },
  {
    surface: "the nav Upgrade button",
    keyPrefix: "nav.upgrade",
    reason:
      "Four server modules end a refusal with \"Tap Upgrade at the top right\". That sentence is a "
      + "promise about this exact control, and it is English until part (d) translates server copy. "
      + "Translating the button first tells her to tap a word that is not on her screen.",
  },
  {
    surface: "the theme control inside My Room's customize modal",
    keyPrefix: "theme.",
    reason:
      "`src/theme.js` owns the theme law and its copy (THEME_HEADING, THEME_SCOPE_NOTE, THEME_LABELS, "
      + "THEME_NOTES). It imports NOTHING, which is what lets r5's strip-loader RUN it, so it cannot "
      + "read this catalog and the words would have to be a HELD CROSSING the way `tier.action.*` is. "
      + "One of its two notes carries an em dash, which this catalog forbids outright — so the crossing "
      + "cannot be held without editing copy that is hers. 14-B4 turned the modal around it and left "
      + "the control alone rather than guessing at her words. It is one flag, not a gap.",
  },
  {
    surface: "the house's own NAMES — the wordmark, the tagline and every room in the roster",
    keyPrefix: "brand.",
    reason:
      "`src/catalogs/rooms.tsx` is the ONE roster that names this house's rooms (Magic Room, Ask Lala "
      + "Room, The Library, Living Room, Seal Creator Room), and `siteMeta.js` owns the wordmark and "
      + "the tagline. Naming her rooms in another language is a BRANDING act, not a translation task: "
      + "those names are held by import in three places (the side menu, the home cards and R42's "
      + "PAGE_TITLES, which is in turn held BYTE FOR BYTE against index.html's static title), and the "
      + "browser tab would still say the English one. 14-B5 turned every DOOR around them and left "
      + "the names alone. It is flagged for her word beside the other scope questions, not guessed at.",
  },
  {
    surface: "the profile photo control in My Room",
    keyPrefix: "photo.",
    reason:
      "Every word it says comes from `src/livingRoom.js` (AVATAR_HELP, AVATAR_POINTER, the three button "
      + "labels) or from `avatarRefusal`, which composes the sentence the SERVER would have sent. That "
      + "is the SERVER's copy by this catalog's own boundary, so it moves with part (d) rather than "
      + "being copied here as a second answer key. It is photographed in outputs/item14 rather than "
      + "described, because a seam inside a room is a thing to look at.",
  },
  // **THE `savedshelf.` ENTRY WAS RETIRED BY 14-B7d(1), NOT DELETED QUIETLY.** Its reason was that
  // the plate she reads was ALSO the DOM id through `shelfSlug` and was composed into English
  // accessible names, so translating the prop on its own would have read "Estante guardado shelf" to
  // a screen reader and moved an anchor id. It ended "the plate turns when the Library does", and
  // this is that round: `Shelf` takes a `label` (painted) apart from its `category` (the id and the
  // data key), and every accessible name around it is a catalog row with a `{name}` token. The plate
  // turns; the id did not move.
  {
    surface: "the astrology room's offering list — every reading this room sells, by name",
    keyPrefix: "offering.",
    reason:
      "`src/catalogs/astrology-menu.ts` is the roster of what the astrology room OFFERS, and its ids "
      + "are load-bearing beyond the words: R31 DERIVES `CHART_TOOL_IDS` from the route table beside "
      + "it, the reading modals take their title and blurb from the same rows, and `REPORT_TYPE_LABELS` "
      + "names the same readings again on her shelf. Turning the names without turning the shelf, the "
      + "PDF kicker and the saved-report modal is the half-translation this layer exists to prevent, "
      + "and turning all four is a round of its own rather than a line inside this one. 14-B6c turned "
      + "every heading ABOVE that list and left the list alone. Flagged for her word, not guessed at.",
  },
  {
    surface: "the Magic Room's books, by name",
    keyPrefix: "magicbook.",
    reason:
      "`src/catalogs/magic-books.ts` is the roster that NAMES this room's books, and it is 14-B5's "
      + "brand ruling one room down: The Spellbook, Greater Keys of Solomon, Books of Moses, Petition, "
      + "Herbs & Tools and Oils are painted on the scene, read out as \"Open {label}\", and repeated as "
      + "the heading of the generator each one opens. Naming them in another language is a branding act, "
      + "not a translation task, and turning the heading without turning the book on the shelf she "
      + "tapped to get there is the half-translation this layer exists to prevent. 14-B7b turned every "
      + "sentence around them. Flagged for her word beside the room names, not guessed at.",
  },
  {
    surface: "the sacred scripts a seal may be inscribed in",
    keyPrefix: "sealscript.",
    reason:
      "`SEAL_LANGUAGES` in `src/magicRoom.js` is `PETITION_DIVINE_NAMES`' shape one size up: what she "
      + "taps in that picker is not a label FOR a value, it IS the value. The label goes on the wire "
      + "in the submission, `sealScriptFor` reads it to pick the script, the resolver is asked for "
      + "that script's forms, and `ringStringsFor` counts inscriptions with it, which is what the "
      + "capacity refusal is arithmetic over. A translated chip is a script the seal cannot be "
      + "inscribed in, and S2 makes that submission the GATE REFERENCE for the whole build.",
  },
  {
    surface: "the Spellbook's category list",
    keyPrefix: "spellcat.",
    reason:
      "A category is not a label on that shelf: it is the `?category=` value the browse request "
      + "carries, it is held to the SERVER'S own SPELL_CATEGORIES and CATALOG_CATEGORIES by "
      + "`t7-catalog.mjs`, and it comes back on every spell row the server sends (rendered again "
      + "beside the title and in the open spell's kicker). Turning the chip would filter on a word "
      + "no spell carries and leave the list beside it in English anyway. It is the `spread.` ruling "
      + "one book over. 14-B7b turned every page around it.",
  },
  {
    surface: "the note under a spirit or an unresolved seal on a recipe",
    keyPrefix: "catalogseal.",
    reason:
      "`catalogSealNote` and `catalogSealToolLine` live in `src/catalogSeals.js`, which imports "
      + "NOTHING (r5's strip-loader entry requirement) and is read by the SERVER as well: "
      + "`server/candle-catalog.js` composes her working's STEPS out of the same sentences. That "
      + "makes them server copy by this catalog's own boundary, so they move with part (d) rather "
      + "than being copied here as a second answer key. It is the `theme.` ruling one module over.",
  },
  {
    surface: "the six aim chips under the Book of Roots' ask box",
    keyPrefix: "herbaim.",
    reason:
      "Money, Healing, Love, Protection, A court case and Cleansing are not labels on that row: tapping "
      + "one POSTs that exact text to /api/herbs as the aim AND writes it into the visible ask box, so a "
      + "translated chip changes what this house asks its own retrieval, which is English herb material. "
      + "Splitting the label from the value would leave the box showing one thing and the request "
      + "carrying another. It is `acg.tools.placeholder`'s argument (a real place typed into a geocoder) "
      + "one room over, and it moves when 14(d) settles what the house says to an engine.",
  },
  {
    surface: "what a thing sitting in the Cauldron is called",
    keyPrefix: "cauldronkind.",
    reason:
      "The Cauldron's list names each item by its KIND (Petition, Greater Keys working, Spell, Oils, "
      + "Conjure Oil, Your seal, Your words), and those are the same names `REPORT_TYPE_LABELS` gives "
      + "the same items on her shelf and `creationName.js` gives them when she has not named one. "
      + "Turning the list without turning the shelf, the PDF kicker and the saved-report modal is the "
      + "half-translation this layer exists to prevent, and turning all four is a round of its own. "
      + "It is the `offering.` ruling applied to the Magic Room's own outputs. "
      + "R50 MOVED THOSE NAMES BUT NOT THIS RULING: they are `CAULDRON_KINDS` in `src/magicRoom.js` "
      + "now, one roster read by the route, the store and the list, with a TOTAL resolver — the "
      + "defect it closed was the fallback (`|| it.kind` showed her the raw machine value), never "
      + "the language. This prefix stays declared and stays unpopulated until that round.",
  },
  {
    surface: "the tarot room's spread and category names",
    keyPrefix: "spread.",
    reason:
      "`src/catalogs/tarot-spreads.ts` is held to the SERVER'S own spread keys (R6-3 imports them "
      + "rather than retyping them), and a category's label rides into `spreadLabel` — which becomes "
      + "the saved reading's TITLE on her shelf and the kicker on its PDF. Turning the label without "
      + "turning both of those is the half-translation this layer exists to prevent. The CARD NAMES "
      + "are a separate and firmer no: the deck is keyed by English slug and TarotCardImage draws "
      + "from it, so \"La Torre\" is a card this house cannot find. 14-B6c turned every word the room "
      + "says around them. Flagged for her word beside the astrology offerings, not guessed at.",
  },
];

// ---------------------------------------------------------------------------------------------
// THE LOOKUP.
// ---------------------------------------------------------------------------------------------

// `{token}` and nothing cleverer. A template language here would be a second thing to gate.
export const PLACEHOLDER_RE = /\{([a-z][a-z0-9]*)\}/gi;

export function placeholders(text) {
  const out = new Set();
  if (typeof text !== "string") return out;
  for (const m of text.matchAll(PLACEHOLDER_RE)) out.add(m[1]);
  return out;
}

export function fillPlaceholders(text, vars) {
  if (typeof text !== "string") return "";
  if (!vars || typeof vars !== "object") return text;
  return text.replace(PLACEHOLDER_RE, (whole, name) => (
    // An unfilled token is left VISIBLE. Replacing it with "" would delete the fact it stands for and
    // leave a sentence that reads as finished.
    Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== undefined && vars[name] !== null
      ? String(vars[name])
      : whole
  ));
}

// The lookup, with the catalog as a PARAMETER so a gate can drive it over a catalog built to break it
// (R26: a law held only against the real data proves the data, not the law).
export function tIn(catalog, code, key, vars) {
  const row = catalog && typeof catalog === "object" ? catalog[key] : null;
  // R17: the receiving side's default must not be silence. The key is not a sentence, and that is the
  // point — it is legible, greppable and reportable, where "" is a hole nobody can describe.
  if (!row || typeof row !== "object") return String(key ?? "");
  const lang = normalizeLanguage(code);
  const own = row[lang];
  const text = typeof own === "string" && own !== "" ? own : row[DEFAULT_LANGUAGE];
  if (typeof text !== "string" || text === "") return String(key ?? "");
  return fillPlaceholders(text, vars);
}

export function t(code, key, vars) {
  return tIn(STRINGS, code, key, vars);
}

export function stringKeys(catalog = STRINGS) {
  return Object.keys(catalog || {});
}

export function hasString(key, catalog = STRINGS) {
  return Object.prototype.hasOwnProperty.call(catalog || {}, key);
}

// ---------------------------------------------------------------------------------------------
// THE LAW THE GATE RUNS. Empty is the only shipping answer.
// ---------------------------------------------------------------------------------------------
export function stringsViolations(catalog = STRINGS, options = {}) {
  const languages = options.languages || LANGUAGES;
  const sameInBoth = options.sameInBoth || SAME_IN_BOTH;
  const englishByDesign = options.englishByDesign || ENGLISH_BY_DESIGN;
  const out = [];
  const rows = catalog && typeof catalog === "object" ? catalog : {};

  for (const [key, row] of Object.entries(rows)) {
    if (!row || typeof row !== "object") {
      out.push(`"${key}" carries no translations at all.`);
      continue;
    }
    // LAW 1a — every key carries every language.
    for (const lang of languages) {
      const text = row[lang];
      if (typeof text !== "string" || text.trim() === "") {
        out.push(`"${key}" has no ${lang} text. Every key carries every language.`);
      }
    }
    // LAW 1b — and nothing else. A stray "fr" is a key half-built for a language this house does not
    // speak, which is how a roster and its mirror drift apart in the first place.
    for (const lang of Object.keys(row)) {
      if (!languages.includes(lang)) {
        out.push(`"${key}" carries "${lang}", which is not a language this house speaks.`);
      }
    }
    // LAW 2 — placeholder parity, against the default language's set.
    const base = placeholders(row[DEFAULT_LANGUAGE]);
    for (const lang of languages) {
      if (lang === DEFAULT_LANGUAGE) continue;
      if (typeof row[lang] !== "string") continue;   // already reported by LAW 1a
      const mine = placeholders(row[lang]);
      for (const token of base) {
        if (!mine.has(token)) out.push(`"${key}" drops {${token}} in ${lang}. A translation keeps every fact the sentence carries.`);
      }
      for (const token of mine) {
        if (!base.has(token)) out.push(`"${key}" invents {${token}} in ${lang}, which nothing fills.`);
      }
    }
    // LAW 3 — an untranslated value, unless it is declared to be the same in both.
    if (!sameInBoth.includes(key)) {
      for (const lang of languages) {
        if (lang === DEFAULT_LANGUAGE) continue;
        if (typeof row[lang] === "string" && typeof row[DEFAULT_LANGUAGE] === "string"
          && row[lang] === row[DEFAULT_LANGUAGE] && row[lang].trim() !== "") {
          out.push(`"${key}" is identical in ${lang} and ${DEFAULT_LANGUAGE}. Declare it on SAME_IN_BOTH or translate it.`);
        }
      }
    }
  }

  // The roster's own half of the biconditional: an exception naming a key that does not exist is an
  // exception nobody can see expire.
  for (const key of sameInBoth) {
    if (!Object.prototype.hasOwnProperty.call(rows, key)) {
      out.push(`SAME_IN_BOTH names "${key}", which is not a string in this catalog.`);
    }
  }

  // LAW 4 — a surface declared ENGLISH BY DESIGN may not quietly grow a key. A half-translated
  // consent block is worse than an English one, because the English one is a decision on the record.
  for (const entry of englishByDesign) {
    const prefix = entry && typeof entry.keyPrefix === "string" ? entry.keyPrefix : "";
    if (!prefix) {
      out.push("ENGLISH_BY_DESIGN carries an entry with no keyPrefix, so it forbids nothing.");
      continue;
    }
    if (!entry.reason || String(entry.reason).trim().length < 40) {
      out.push(`ENGLISH_BY_DESIGN's "${entry.surface || prefix}" carries no reason, and a reason is what makes it a decision rather than a gap.`);
    }
    for (const key of Object.keys(rows)) {
      if (key.startsWith(prefix)) {
        out.push(`"${key}" is filed under "${prefix}", which is declared ENGLISH BY DESIGN (${entry.surface || prefix}). Translate the whole surface or delete the declaration.`);
      }
    }
  }

  return out;
}
